// =============================================================================
//  Catalogue de contrôles NIS2 / ISO 27001 pour Microsoft 365.
//
//  v1.1 - 35 contrôles couvrant les 4 familles + CyFun Basic gap closure :
//     - Identite & acces  (ID-xxx)  -> 13 contrôles (8 v1 + 5 CyFun Basic)
//     - Email & phishing  (EM-xxx)  -> 6 contrôles
//     - Protection données (DP-xxx) -> 9 contrôles (6 v1 + 3 CyFun Basic)
//     - Logs & monitoring (LG-xxx)  -> 7 contrôles (5 v1 + 2 CyFun Basic)
//
//  v1.1 ajoute 10 contrôles pour atteindre 100% du niveau Basic CyFun :
//     - ID-009 (PR.AC-3.2 remote MFA), ID-010 (PR.AC-5.1 named locations),
//       ID-011 (PR.AC-5.2 segmentation), ID-012 (PR.IP-11.1 admins actifs),
//       ID-013 (PR.MA-1.1 office updates)
//     - DP-007 (ID.AM-5.1 sensitivity labels), DP-008 (PR.DS-3.1 disposition),
//       DP-009 (PR.IP-4.1 backup retention)
//     - LG-006 (ID.RA-5.1 secure score), LG-007 (DE.CM-3.1 defender endpoint)
//
//  Sources de mapping :
//    - Directive NIS2 (UE 2022/2555) Article 21(2) - mesures de gestion des
//      risques en matiere de cybersecurite
//    - ISO/IEC 27001:2022 Annexe A
//    - CIS Microsoft 365 Foundations Benchmark v3.x (pour la valeur "comment
//      vérifier")
//
//  Chaque contrôle reference les endpoints Microsoft Graph qu'on interrogerait
//  dans une implémentation reelle. En MVP, ces endpoints sont simules par
//  scanEngine.ts qui retourne des réponses plausibles.
// =============================================================================

import { ControlDefinition } from './types';

export const CATALOG_VERSION = '2026.06.8';

export const controls: ControlDefinition[] = [
  // ===========================================================================
  // IDENTITÉ & ACCÈS
  // ===========================================================================
  {
    id: 'ID-001',
    family: 'identity',
    title: 'Couverture MFA des utilisateurs',
    description:
      'Au moins 95 % des comptes actifs disposent d\'une méthode MFA enregistree (authenticator app, FIDO2, ou Windows Hello).',
    rationale:
      'L\'authentification multifacteur est la mesure unique qui bloque le plus d\'attaques par compromission d\'identifiants. NIS2 art. 21(2)(j) en fait une obligation explicite.',
    severity: 'critical',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(j)', label: 'Utilisation de l\'authentification multifacteur' },
      { framework: 'ISO27001',  ref: 'A.8.5',         label: 'Authentification sécurisée' },
      { framework: 'CIS-M365',  ref: '1.1.1',         label: 'Ensure MFA is enabled for all admin accounts' },
      { framework: 'CyFun',     ref: 'PR.AC-1',       label: 'Identités & credentials gérés (PR.AC-1.4 = MFA explicite)', cyfunLevel: 'Essential' },
      { framework: 'CyFun',     ref: 'PR.AC-7',       label: 'Users authenticated commensurate with risk',                cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/reports/authenticationMethods/userRegistrationDetails'],
    remediation:
      'Activer une Conditional Access Policy "Require MFA for all users" ou les Security Defaults. Inscrire les utilisateurs restants via campagne de bascule MFA.',
    manual: false,
  },
  {
    id: 'ID-002',
    family: 'identity',
    title: 'Conditional Access ou Security Defaults imposant MFA',
    description:
      'Il existe au moins une Conditional Access Policy active qui exige MFA pour tous les utilisateurs, ou les Security Defaults sont actives.',
    rationale:
      'Inscrire MFA ne suffit pas - encore faut-il que les sign-ins l\'exigent. Sans CA Policy, les comptes peuvent contourner MFA.',
    severity: 'critical',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(j)', label: 'Authentification multifacteur' },
      { framework: 'ISO27001',  ref: 'A.5.15',        label: 'Contrôle d\'accès' },
      { framework: 'CIS-M365',  ref: '1.1.2',         label: 'Conditional Access enforces MFA' },
      { framework: 'CyFun',     ref: 'PR.AC-1',       label: 'Identités & credentials gérés (PR.AC-1.4 = MFA)',  cyfunLevel: 'Essential' },
      { framework: 'CyFun',     ref: 'PR.AC-7',       label: 'Users authenticated commensurate with risk',       cyfunLevel: 'Important' },
    ],
    graphEndpoints: [
      '/identity/conditionalAccess/policies',
      '/policies/identitySecurityDefaultsEnforcementPolicy',
    ],
    remediation:
      'Créer une CA Policy : Users=All / Cloud apps=All / Grant=Require MFA / State=On. Exclure uniquement le compte break-glass.',
    manual: false,
  },
  {
    id: 'ID-003',
    family: 'identity',
    title: 'Nombre de Global Administrators',
    description:
      'Le tenant compte entre 2 et 4 Global Administrators (2 minimum pour la resilience, 4 maximum pour limiter la surface d\'attaque).',
    rationale:
      'Trop d\'admins augmente le risque ; trop peu (1 seul) cree un single point of failure opérationnel.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(i)', label: 'Politiques de contrôle d\'accès' },
      { framework: 'ISO27001',  ref: 'A.8.2',         label: 'Droits d\'accès privilégiés' },
      { framework: 'CIS-M365',  ref: '1.1.3',         label: 'Designated 2-4 Global Admins' },
      { framework: 'CyFun',     ref: 'PR.AC-4',       label: 'Least privilege & SoD (PR.AC-4.4 : nobody admin for daily tasks)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/directoryRoles', '/roleManagement/directory/roleAssignments'],
    remediation:
      'Retrograder les Global Admins en exces vers des roles plus spécifiques (Exchange Admin, SharePoint Admin, etc.). Garder 2 GA "break-glass" hors PIM.',
    manual: false,
  },
  {
    id: 'ID-004',
    family: 'identity',
    title: 'Privileged Identity Management (PIM) activement utilise',
    description:
      'Au moins 80 % des roles privilégiés sont assignes en mode eligible (JIT) plutot que permanent via PIM.',
    rationale:
      'Réduit la fenêtre d\'exposition des comptes admins. Force la justification et le log des elevations.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(i)', label: 'Contrôle d\'accès' },
      { framework: 'ISO27001',  ref: 'A.8.2',         label: 'Droits d\'accès privilégiés' },
      { framework: 'CIS-M365',  ref: '1.1.4',         label: 'Use PIM for privileged roles' },
      { framework: 'CyFun',     ref: 'PR.AC-4',       label: 'Least privilege (PR.AC-4.7 Important : privileged users monitored)', cyfunLevel: 'Important' },
      { framework: 'CyFun',     ref: 'PR.AT-2',       label: 'Privileged users qualifies et connaissent leurs roles',              cyfunLevel: 'Important' },
    ],
    graphEndpoints: [
      '/roleManagement/directory/roleAssignmentSchedules',
      '/roleManagement/directory/roleEligibilitySchedules',
    ],
    remediation:
      'Convertir les role assignments permanents en eligible via PIM. Imposer MFA + justification pour activation. Approbation requise pour Global Admin.',
    manual: false,
  },
  {
    id: 'ID-005',
    family: 'identity',
    title: 'Authentification legacy bloquee',
    description:
      'Une Conditional Access Policy bloque les protocoles d\'authentification legacy (POP, IMAP, SMTP AUTH, ActiveSync basic).',
    rationale:
      'Les protocoles legacy ne supportent pas MFA et representent le vecteur principal du password spraying.',
    severity: 'critical',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(g)', label: 'Hygiene cyber de base' },
      { framework: 'ISO27001',  ref: 'A.8.5',         label: 'Authentification sécurisée' },
      { framework: 'CIS-M365',  ref: '1.1.5',         label: 'Block legacy authentication' },
      { framework: 'CyFun',     ref: 'PR.AC-7',       label: 'Users authenticated commensurate with risk (legacy = pas MFA possible)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation:
      'Créer une CA Policy : Conditions=Client apps=Exchange ActiveSync + Other clients / Access=Block / State=On.',
    manual: false,
  },
  {
    id: 'ID-006',
    family: 'identity',
    title: 'Reinitialisation de mot de passe en libre-service (SSPR)',
    description:
      'SSPR est active et requiert au moins 2 méthodes de vérification distinctes.',
    rationale:
      'Réduit les tickets helpdesk et evite les resets non securises par telephone (ingenierie sociale).',
    severity: 'medium',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(g)', label: 'Hygiene cyber de base' },
      { framework: 'ISO27001',  ref: 'A.5.17',        label: 'Informations d\'authentification' },
      { framework: 'CIS-M365',  ref: '1.3.1',         label: 'Enable SSPR' },
      { framework: 'CyFun',     ref: 'PR.AC-1',       label: 'Credentials gérés (PR.AC-1.2 Important : mecanismes automatises)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/policies/authorizationPolicy', '/policies/authenticationMethodsPolicy'],
    remediation:
      'Azure AD > Password reset > Properties : Self service password reset enabled = All. Authentication methods : 2 méthodes requises.',
    manual: false,
  },
  {
    id: 'ID-007',
    family: 'identity',
    title: 'Aucun compte avec mot de passe expirant jamais',
    description:
      'Aucun compte (hors comptes de service identifies) n\'a la propriété "Password never expires".',
    rationale:
      'Si un mot de passe statique fuite, l\'attaquant garde l\'accès indefiniment.',
    severity: 'medium',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(g)', label: 'Hygiene cyber de base' },
      { framework: 'ISO27001',  ref: 'A.5.17',        label: 'Informations d\'authentification' },
      { framework: 'CyFun',     ref: 'PR.AC-1',       label: 'Credentials gérés (PR.AC-1.3 Essential : deactivation périodique)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: ['/users?$filter=passwordPolicies eq \'DisablePasswordExpiration\''],
    remediation:
      'Migrer les comptes concernes vers la rotation automatique. Pour les comptes de service legitimes, documenter et basculer vers une managed identity quand possible.',
    manual: false,
  },
  {
    id: 'ID-008',
    family: 'identity',
    title: 'Accès invites revus périodiquement',
    description:
      'Une Access Review est configuree pour les invites (guests) avec un cycle d\'au moins 90 jours.',
    rationale:
      'Les invites s\'accumulent et conservent l\'accès a SharePoint/Teams longtemps après la fin de la collaboration.',
    severity: 'medium',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(d)', label: 'Sécurité de la chaine d\'approvisionnement' },
      { framework: 'ISO27001',  ref: 'A.5.18',        label: 'Droits d\'accès' },
      { framework: 'CyFun',     ref: 'PR.AC-4',       label: 'Access permissions revues (PR.AC-4.6 SoD)',          cyfunLevel: 'Important' },
      { framework: 'CyFun',     ref: 'PR.AC-6',       label: 'Identités verifiees avant emission de credentials',  cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/identityGovernance/accessReviews/definitions'],
    remediation:
      'Entra ID > Identity Governance > Access reviews : créer une review trimestrielle ciblant tous les Guest users avec auto-removal si non valide.',
    manual: false,
  },

  // ===========================================================================
  // EMAIL & ANTI-PHISHING
  // ===========================================================================
  {
    id: 'EM-001',
    family: 'email',
    title: 'Politique anti-phishing avec protection contre l\'usurpation',
    description:
      'Une politique anti-phishing (Defender for Office 365) est active avec protection des utilisateurs et domaines impersones.',
    rationale:
      'Le spear-phishing usurpant le CEO ou un partenaire est le vecteur principal des fraudes au president.',
    severity: 'critical',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(g)', label: 'Hygiene cyber de base' },
      { framework: 'ISO27001',  ref: 'A.5.14',        label: 'Transfert d\'information' },
      { framework: 'CIS-M365',  ref: '2.1.5',         label: 'Anti-phishing protection enabled' },
      { framework: 'CyFun',     ref: 'DE.CM-4',       label: 'Malicious code detected (anti-phishing)', cyfunLevel: 'Basic' },
      { framework: 'CyFun',     ref: 'PR.AT-1',       label: 'Users trained (phishing awareness)',     cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/security/emailThreatSubmissionPolicies', '(Defender XDR)'],
    remediation:
      'Defender > Email & collaboration > Policies > Anti-phishing : Standard ou Strict preset. Ajouter les utilisateurs VIP en "Users to protect".',
    manual: false,
  },
  {
    id: 'EM-002',
    family: 'email',
    title: 'Safe Attachments active',
    description:
      'Safe Attachments est active pour Exchange, SharePoint, OneDrive et Teams avec action "Block".',
    rationale:
      'Analyse sandbox des pieces jointes avant remise. Bloque les malwares zero-day non detectes par les signatures AV.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(e)', label: 'Sécurité des systèmes - vulnerabilites' },
      { framework: 'ISO27001',  ref: 'A.8.7',         label: 'Protection contre les logiciels malveillants' },
      { framework: 'CIS-M365',  ref: '2.1.4',         label: 'Safe Attachments enabled' },
      { framework: 'CyFun',     ref: 'DE.CM-4',       label: 'Malicious code detected (sandbox attachments)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['(Defender for Office 365)'],
    remediation:
      'Defender > Policies > Safe Attachments : créer une politique avec Action=Block. Activer Safe Documents pour les utilisateurs E5.',
    manual: false,
  },
  {
    id: 'EM-003',
    family: 'email',
    title: 'Safe Links active',
    description:
      'Safe Links est active pour Email, Teams et Office apps, avec time-of-click protection.',
    rationale:
      'Re-evalue les URLs au moment du clic, attrape les liens qui pointaient initialement vers du contenu propre.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(g)', label: 'Hygiene cyber de base' },
      { framework: 'ISO27001',  ref: 'A.8.23',        label: 'Filtrage des sites web' },
      { framework: 'CIS-M365',  ref: '2.1.3',         label: 'Safe Links enabled' },
      { framework: 'CyFun',     ref: 'DE.CM-4',       label: 'Malicious code detected (URL scanning)',      cyfunLevel: 'Basic' },
      { framework: 'CyFun',     ref: 'PR.PT-4',       label: 'Web/email filters installed (PR.PT-4.1)',    cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['(Defender for Office 365)'],
    remediation:
      'Defender > Policies > Safe Links : preset Strict ou custom avec "Click protection settings" actives pour toutes les apps.',
    manual: false,
  },
  {
    id: 'EM-004',
    family: 'email',
    title: 'DKIM configure sur les domaines acceptes',
    description:
      'DKIM est active (selector1 + selector2) pour tous les domaines acceptes ayant un MX vers Exchange Online.',
    rationale:
      'DKIM permet aux destinataires de vérifier l\'authenticite de l\'expediteur ; pré-requis pour DMARC.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(h)', label: 'Cryptographie' },
      { framework: 'ISO27001',  ref: 'A.8.24',        label: 'Utilisation de la cryptographie' },
      { framework: 'CIS-M365',  ref: '2.1.9',         label: 'DKIM enabled for all domains' },
      { framework: 'CyFun',     ref: 'PR.AC-1',       label: 'Identités verifiees (anti-spoofing email)', cyfunLevel: 'Important' },
      { framework: 'CyFun',     ref: 'PR.DS-2',       label: 'Data in transit protege (intégrité mail)',  cyfunLevel: 'Essential' },
    ],
    graphEndpoints: ['/domains', '(Exchange Online PowerShell : Get-DkimSigningConfig)'],
    remediation:
      'Defender > Email auth settings > DKIM : pour chaque domaine, publier les 2 CNAMEs puis activer.',
    manual: false,
  },
  {
    id: 'EM-005',
    family: 'email',
    title: 'DMARC en politique reject ou quarantine',
    description:
      'L\'enregistrement DMARC du domaine principal est en politique p=quarantine ou p=reject (pas p=none).',
    rationale:
      'p=none ne bloque rien - l\'usurpation reste possible. p=quarantine envoie en spam, p=reject rejette.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(h)', label: 'Cryptographie' },
      { framework: 'ISO27001',  ref: 'A.5.14',        label: 'Transfert d\'information' },
      { framework: 'CIS-M365',  ref: '2.1.10',        label: 'DMARC policy is quarantine or reject' },
      { framework: 'CyFun',     ref: 'PR.AC-1',       label: 'Identités verifiees (DMARC enforcement)',    cyfunLevel: 'Important' },
      { framework: 'CyFun',     ref: 'PR.DS-2',       label: 'Data in transit protege (auth email)',       cyfunLevel: 'Essential' },
    ],
    graphEndpoints: ['(DNS TXT lookup : _dmarc.<domain>)'],
    remediation:
      'Publier un TXT _dmarc.<domain> : v=DMARC1; p=quarantine; rua=mailto:dmarc@<domain>; pct=100. Monter en p=reject après 90j d\'observation des rapports.',
    manual: false,
  },
  {
    id: 'EM-006',
    family: 'email',
    title: 'Enregistrement SPF hardened (-all)',
    description:
      'Le SPF du domaine principal se termine par -all (hard fail) et n\'inclut que les sources legitimes.',
    rationale:
      '~all (soft fail) laisse passer beaucoup de phishing ; -all force le rejet par les destinataires conformes.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(g)', label: 'Hygiene cyber de base' },
      { framework: 'ISO27001',  ref: 'A.5.14',        label: 'Transfert d\'information' },
      { framework: 'CIS-M365',  ref: '2.1.8',         label: 'SPF record exists and is hardened' },
      { framework: 'CyFun',     ref: 'PR.AC-1',       label: 'Identités verifiees (envoyeurs autorises)', cyfunLevel: 'Important' },
      { framework: 'CyFun',     ref: 'PR.DS-2',       label: 'Data in transit protege',                   cyfunLevel: 'Essential' },
    ],
    graphEndpoints: ['(DNS TXT lookup : <domain>)'],
    remediation:
      'Publier un TXT racine : v=spf1 include:spf.protection.outlook.com -all. Auditer prealablement les expediteurs tiers (newsletters, CRM).',
    manual: false,
  },

  // ===========================================================================
  // PROTECTION DES DONNEES
  // ===========================================================================
  {
    id: 'DP-001',
    family: 'data',
    title: 'Politique DLP pour informations sensibles (PII, CB)',
    description:
      'Au moins une politique DLP est active couvrant Exchange, SharePoint, OneDrive et Teams, ciblant numéros de carte de credit, IBAN et données personnelles UE.',
    rationale:
      'Detecte et bloque les fuites accidentelles de données regulees (RGPD, PCI-DSS).',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(h)', label: 'Protection des données' },
      { framework: 'ISO27001',  ref: 'A.8.12',        label: 'Prevention de fuite de données' },
      { framework: 'CIS-M365',  ref: '3.1.1',         label: 'DLP policy enabled' },
      { framework: 'CyFun',     ref: 'PR.DS-5',       label: 'Protections against data leaks implemented (PR.DS-5.1)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['(Purview compliance : Get-DlpCompliancePolicy)'],
    remediation:
      'Purview > Data loss prevention > Policies : appliquer le template "EU PII + Financial Data" sur tous les workloads. Mode Test au demarrage, puis Enforce.',
    manual: false,
  },
  {
    id: 'DP-002',
    family: 'data',
    title: 'Sensitivity labels deployes et publies',
    description:
      'Au moins 3 sensitivity labels sont publies pour les utilisateurs (Public / Internal / Confidential minimum) avec chiffrement pour le label le plus eleve.',
    rationale:
      'Permet la classification systematique et le chiffrement des documents les plus sensibles, suivi même hors du tenant.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(h)', label: 'Cryptographie' },
      { framework: 'ISO27001',  ref: 'A.5.13',        label: 'Etiquetage de l\'information' },
      { framework: 'CIS-M365',  ref: '3.2.1',         label: 'Sensitivity labels published' },
      { framework: 'CyFun',     ref: 'PR.DS-5',       label: 'Data leaks prevention via classification', cyfunLevel: 'Important' },
      { framework: 'CyFun',     ref: 'PR.AC-4',       label: 'Least privilege (accès selon label)',      cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/security/informationProtection/labelPolicySettings'],
    remediation:
      'Purview > Information protection > Labels : créer Public / Internal / Confidential / Highly Confidential. Publier via label policy. Chiffrer "Highly Confidential" avec Co-Author rights.',
    manual: false,
  },
  {
    id: 'DP-003',
    family: 'data',
    title: 'Partage externe SharePoint restreint',
    description:
      'Le partage externe SharePoint est configure sur "New and existing guests" ou plus restrictif (pas "Anyone").',
    rationale:
      '"Anyone" cree des liens anonymes non revocables qui restent indexables. Vecteur majeur de fuite.',
    severity: 'critical',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(i)', label: 'Gestion des actifs' },
      { framework: 'ISO27001',  ref: 'A.5.10',        label: 'Utilisation acceptable de l\'information' },
      { framework: 'CIS-M365',  ref: '7.2.1',         label: 'Restrict external sharing' },
      { framework: 'CyFun',     ref: 'PR.AC-4',       label: 'Access permissions controlees (partage externe)', cyfunLevel: 'Basic' },
      { framework: 'CyFun',     ref: 'PR.DS-5',       label: 'Data leaks prevention (Anyone-link bloque)',     cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['(SharePoint Admin : Get-SPOTenant)'],
    remediation:
      'SharePoint Admin > Policies > Sharing : selectionner "New and existing guests" ou "Only people in your organization". Limiter par domaines si possible.',
    manual: false,
  },
  {
    id: 'DP-004',
    family: 'data',
    title: 'Partage externe OneDrive aligne',
    description:
      'Le partage externe OneDrive est aligne ou plus restrictif que celui de SharePoint (pas plus permissif).',
    rationale:
      'OneDrive peut court-circuiter la politique SharePoint si configure separement. Doit être aligne.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(i)', label: 'Gestion des actifs' },
      { framework: 'ISO27001',  ref: 'A.5.10',        label: 'Utilisation acceptable' },
      { framework: 'CIS-M365',  ref: '7.2.2',         label: 'OneDrive sharing aligned' },
      { framework: 'CyFun',     ref: 'PR.AC-4',       label: 'Access permissions homogenes (OneDrive == SharePoint)', cyfunLevel: 'Basic' },
      { framework: 'CyFun',     ref: 'PR.DS-5',       label: 'Data leaks prevention coherente',                       cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['(SharePoint Admin : Get-SPOTenant -OneDriveSharingCapability)'],
    remediation:
      'SharePoint Admin > Settings > OneDrive : aligner sur le niveau SharePoint. "Limit external sharing by domain" si liste blanche partenaires.',
    manual: false,
  },
  {
    id: 'DP-005',
    family: 'data',
    title: 'Politiques de retention en place',
    description:
      'Au moins une politique de retention couvre les mailboxes et les sites SharePoint avec une duree minimale de 3 ans.',
    rationale:
      'Obligation legale dans la plupart des secteurs. Également utile pour la recuperation après ransomware.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(c)', label: 'Continuite des activites' },
      { framework: 'ISO27001',  ref: 'A.5.10',        label: 'Utilisation acceptable' },
      { framework: 'CIS-M365',  ref: '6.1.1',         label: 'Retention policies in place' },
      { framework: 'CyFun',     ref: 'ID.GV-1',       label: 'Policy retention documentée',                     cyfunLevel: 'Basic' },
      { framework: 'CyFun',     ref: 'ID.GV-3',       label: 'Legal/regulatory requirements re. retention',     cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['(Purview compliance : Get-RetentionCompliancePolicy)'],
    remediation:
      'Purview > Data lifecycle management > Retention policies : créer une politique couvrant Exchange + SharePoint + OneDrive avec retention >= 7 ans pour fonctions reglementees.',
    manual: false,
  },
  {
    id: 'DP-006',
    family: 'data',
    title: 'Chiffrement BitLocker exige (Intune)',
    description:
      'Une politique Intune Compliance ou Configuration impose BitLocker chiffre sur tous les postes Windows joints.',
    rationale:
      'En cas de vol/perte, le chiffrement disque rend les données inexploitables. Exigence implicite pour declarer un incident "sans fuite".',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(h)', label: 'Cryptographie' },
      { framework: 'ISO27001',  ref: 'A.8.24',        label: 'Utilisation de la cryptographie' },
      { framework: 'CIS-M365',  ref: '4.4.1',         label: 'Disk encryption enforced' },
      { framework: 'CyFun',     ref: 'PR.DS-1',       label: 'Data at rest protege (chiffrement disque)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [
      '/deviceManagement/deviceCompliancePolicies',
      '/deviceManagement/managedDevices',
    ],
    remediation:
      'Intune > Devices > Compliance policies > Windows : Require BitLocker = Yes. Lier au CA pour exclure les postes non chiffres.',
    manual: false,
  },

  // ===========================================================================
  // LOGS & MONITORING
  // ===========================================================================
  {
    id: 'LG-001',
    family: 'logs',
    title: 'Unified Audit Log active',
    description:
      'Le Unified Audit Log est active au niveau du tenant.',
    rationale:
      'Sans UAL, aucune trace des actions admin/utilisateur n\'est conservee - investigation post-incident impossible.',
    severity: 'critical',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Gestion des incidents' },
      { framework: 'ISO27001',  ref: 'A.8.15',        label: 'Journalisation' },
      { framework: 'CIS-M365',  ref: '5.1.1',         label: 'Unified Audit Log enabled' },
      { framework: 'CyFun',     ref: 'PR.PT-1',       label: 'Audit/log records determined and reviewed (PR.PT-1.1)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/admin/serviceAnnouncement', '(Exchange : Get-AdminAuditLogConfig)'],
    remediation:
      'Purview > Audit > Start recording user and admin activity. Vérifier UnifiedAuditLogIngestionEnabled=True via PowerShell.',
    manual: false,
  },
  {
    id: 'LG-002',
    family: 'logs',
    title: 'Retention des logs >= 1 an',
    description:
      'La duree de retention des logs d\'audit et de sign-in est d\'au moins 1 an (E5 / Audit Premium).',
    rationale:
      'NIS2 exige la capacite d\'investigation sur 1 an minimum. La retention par defaut E3 (90 jours) est insuffisante.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 23',       label: 'Obligations de notification d\'incidents' },
      { framework: 'ISO27001',  ref: 'A.8.15',        label: 'Journalisation' },
      { framework: 'CyFun',     ref: 'PR.PT-1',       label: 'Audit logs avec time-stamps (PR.PT-1.2 Important)', cyfunLevel: 'Important' },
      { framework: 'CyFun',     ref: 'ID.GV-3',       label: 'Legal requirements re. duree de retention logs',   cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['(Purview Audit : Get-RetentionPolicy)'],
    remediation:
      'Acquerir Audit Premium (inclus E5/G5) ou export vers Sentinel/SIEM tiers avec retention 1 an minimum.',
    manual: false,
  },
  {
    id: 'LG-003',
    family: 'logs',
    title: 'Alertes sur sign-ins risques',
    description:
      'Des alertes sont configurees pour les sign-ins detectes comme "High risk" par Identity Protection.',
    rationale:
      'Détection rapide des comptes compromis. Sans alerte, le risque reste dormant.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Gestion des incidents' },
      { framework: 'ISO27001',  ref: 'A.8.16',        label: 'Activites de surveillance' },
      { framework: 'CIS-M365',  ref: '5.2.3',         label: 'Risky sign-in alerts configured' },
      { framework: 'CyFun',     ref: 'DE.CM-7',       label: 'Monitoring for unauthorized access',          cyfunLevel: 'Important' },
      { framework: 'CyFun',     ref: 'DE.AE-3',       label: 'Event data correlated from multiple sources', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/identityProtection/riskyUsers', '/identityProtection/riskDetections'],
    remediation:
      'Entra ID > Security > Identity Protection > Users at risk policy : Risk level=High / Access=Require password change. Alert policy email vers SOC.',
    manual: false,
  },
  {
    id: 'LG-004',
    family: 'logs',
    title: 'Defender for Cloud Apps connecte (MCAS)',
    description:
      'Defender for Cloud Apps (ex-MCAS) est connecte au tenant pour la surveillance des activites SaaS.',
    rationale:
      'Detecte les anomalies comportementales (impossible travel, mass download) au dela des Sign-in logs Azure AD.',
    severity: 'medium',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(b)', label: 'Gestion des incidents' },
      { framework: 'ISO27001',  ref: 'A.8.16',        label: 'Activites de surveillance' },
      { framework: 'CyFun',     ref: 'DE.CM-1',       label: 'Network monitored (SaaS apps inclus)', cyfunLevel: 'Basic' },
      { framework: 'CyFun',     ref: 'DE.CM-7',       label: 'Unauthorized connections/devices monitored', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['(Defender XDR : security.microsoft.com)'],
    remediation:
      'Connecter Defender for Cloud Apps via Defender XDR > Settings > Cloud Apps. Activer "Office 365" et "Microsoft Azure" comme app connectors.',
    manual: false,
  },
  {
    id: 'LG-005',
    family: 'logs',
    title: 'Playbook de réponse aux incidents documente',
    description:
      'Un playbook ecrit decrit la procédure de réponse aux incidents : détection, notification interne, decision de notification ANSSI/CSIRT (24h NIS2), confinement, post-mortem.',
    rationale:
      'NIS2 art. 23 impose la notification d\'incident sous 24h. Sans playbook, le delai n\'est jamais tenu.',
    severity: 'critical',
    references: [
      { framework: 'NIS2',      ref: 'Art. 23',       label: 'Obligations de notification' },
      { framework: 'ISO27001',  ref: 'A.5.24',        label: 'Planification de la gestion des incidents' },
      { framework: 'CyFun',     ref: 'RS.RP-1',       label: 'Response plan execute pendant incident', cyfunLevel: 'Basic' },
      { framework: 'CyFun',     ref: 'RS.CO-1',       label: 'Personnel connait roles et ordre operations', cyfunLevel: 'Important' },
      { framework: 'CyFun',     ref: 'RS.CO-2',       label: 'Incidents reportes selon critères etablis (24h NIS2)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation:
      'Rediger ou adopter un playbook IR (template NIST SP 800-61 ou ANSSI). Faire un exercice tabletop par an. Stocker dans un emplacement accessible hors-bande.',
    manual: true,
  },

  // ===========================================================================
  // CyFun Basic gap closure - contrôles ajoutes pour couvrir 100% du Basic CCB
  // (mapping etabli dans nis365-cyfun-coverage-map.xlsx, sheet GAP-Basic)
  // ===========================================================================
  {
    id: 'ID-009',
    family: 'identity',
    title: 'MFA pour les accès distants (Conditional Access)',
    description:
      'Au moins une Conditional Access Policy active exige MFA pour les sign-ins distants (apps cloud, hors emplacements fiables).',
    rationale:
      'Les accès distants sont la 1ere surface d attaque par credential stuffing/phishing. CyFun PR.AC-3.2 (Basic) impose MFA pour tout accès distant.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(j)', label: 'Authentification multifacteur' },
      { framework: 'ISO27001',  ref: 'A.8.5',         label: 'Authentification sécurisée' },
      { framework: 'CIS-M365',  ref: '1.1.2',         label: 'MFA enforced via Conditional Access' },
      { framework: 'CyFun',     ref: 'PR.AC-3',       label: 'Remote access managed (PR.AC-3.2 = MFA pour accès distant)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation:
      'Créer une CA Policy : Users=All / Cloud apps=All / Conditions=Untrusted locations / Grant=Require MFA / State=On.',
    manual: false,
  },
  {
    id: 'ID-010',
    family: 'identity',
    title: 'Named Locations définies (périmètre réseau logique)',
    description:
      'Au moins une Named Location est définie dans Conditional Access, dont au moins une marquee "trusted".',
    rationale:
      'M365 ne contrôle pas les firewalls réseau. Les Named Locations sont la meilleure approximation : elles materialisent un périmètre logique (sites/IPs trusted) requis par CyFun PR.AC-5.1.',
    severity: 'medium',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(i)', label: 'Contrôle d accès / segmentation' },
      { framework: 'ISO27001',  ref: 'A.8.20',        label: 'Sécurité des réseaux' },
      { framework: 'CyFun',     ref: 'PR.AC-5',       label: 'Network integrity protected (PR.AC-5.1 = firewalls)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/namedLocations'],
    remediation:
      'Conditional Access > Named locations > Configure. Ajouter au moins le siège/bureau principal en "trusted location" puis l utiliser comme condition dans les CA Policies.',
    manual: false,
  },
  {
    id: 'ID-011',
    family: 'identity',
    title: 'Segmentation logique des accès (CA par groupes/roles)',
    description:
      'Les Conditional Access Policies actives utilisent plusieurs groupes ou roles distincts comme cibles, demontrant une differenciation des règles selon les populations.',
    rationale:
      'CyFun PR.AC-5.2 (Basic) attend une segregation des accès aux systèmes critiques. Dans M365, cela se materialise par des CA Policies differentes pour admins, devs, users standards, externes.',
    severity: 'medium',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(i)', label: 'Politiques d accès differenciees' },
      { framework: 'ISO27001',  ref: 'A.8.3',         label: 'Restriction d accès a l information' },
      { framework: 'CyFun',     ref: 'PR.AC-5',       label: 'Network integrity protected (PR.AC-5.2 = segmentation)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation:
      'Définir des CA Policies distinctes : "Admins (strict MFA + compliant device)", "Users std (MFA risk-based)", "Externes (block by default)", etc. Au minimum 2 groupes/roles distincts cibles.',
    manual: false,
  },
  {
    id: 'ID-012',
    family: 'identity',
    title: 'Comptes Global Admin actifs et verifies',
    description:
      'Tous les comptes Global Administrator ont une activite de sign-in dans les 60 derniers jours - aucun compte admin dormant.',
    rationale:
      'CyFun PR.IP-11.1 (Basic) attend que les personnels avec accès critique soient verifies. Les comptes admin dormants sont un vecteur d attaque (mots de passe oublies, comptes ex-employes).',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(i)', label: 'Gestion des accès privilégiés' },
      { framework: 'ISO27001',  ref: 'A.8.2',         label: 'Droits d accès privilégiés' },
      { framework: 'CIS-M365',  ref: '1.1.4',         label: 'Review of admin account activity' },
      { framework: 'CyFun',     ref: 'PR.IP-11',      label: 'Cybersecurity in HR (PR.IP-11.1 = personnel critique vérifié)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/directoryRoles', '/users/{id}?$select=signInActivity'],
    remediation:
      'Identifier les comptes GA inactifs > 60j. Pour chaque : confirmer l identité du proprietaire et utilite + reactivation, OU desactivation + retrait du role.',
    manual: false,
  },
  {
    id: 'ID-013',
    family: 'identity',
    title: 'Gestion centralisee des updates Office Apps',
    description:
      'Le tenant dispose de licences M365 Apps + Intune pour gerer centralement les updates Office (Monthly Enterprise channel recommande).',
    rationale:
      'CyFun PR.MA-1.1 (Basic) attend que les patches et updates de sécurité soient appliques. Sans Intune, l audit endpoint est impossible et le statut reste "manual review".',
    severity: 'medium',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(e)', label: 'Sécurité acquisition/maintenance des SI' },
      { framework: 'ISO27001',  ref: 'A.8.8',         label: 'Gestion des vulnerabilites techniques' },
      { framework: 'CIS-M365',  ref: '2.2.x',         label: 'Office Apps update channel set to Monthly Enterprise' },
      { framework: 'CyFun',     ref: 'PR.MA-1',       label: 'Maintenance and repair performed (PR.MA-1.1 = patches OS+critiques)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/subscribedSkus'],
    remediation:
      'Souscrire a Microsoft Intune (P1 ou via M365 E3+) pour gerer les configs Office Apps. Sinon : config Office UpdatesAuto via GPO/registry et documenter le processus.',
    manual: false,
  },
  {
    id: 'DP-007',
    family: 'data',
    title: 'Sensitivity Labels publies (classification des actifs)',
    description:
      'Au moins 3 Sensitivity Labels actifs sont publies (typiquement Public / Internal / Confidential / Strictly Confidential).',
    rationale:
      'CyFun ID.AM-5.1 (Basic) attend que les actifs (données, systèmes) soient priorises selon leur classification et valeur business. Dans M365, les Sensitivity Labels sont l outil de classification natif.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(d)', label: 'Gestion des risques + classification' },
      { framework: 'ISO27001',  ref: 'A.5.12',        label: 'Classification de l information' },
      { framework: 'CIS-M365',  ref: '3.1.x',         label: 'Sensitivity Labels deployed' },
      { framework: 'CyFun',     ref: 'ID.AM-5',       label: 'Resources prioritized by classification (ID.AM-5.1)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/beta/security/informationProtection/sensitivityLabels'],
    remediation:
      'Microsoft Purview > Information Protection > Labels. Créer/publier les labels Public/Internal/Confidential/Strictly Confidential avec actions auto (watermark, encryption).',
    manual: false,
  },
  {
    id: 'DP-008',
    family: 'data',
    title: 'Politiques de retention et disposition définies',
    description:
      'Au moins un retention label est défini avec une action de fin de vie (disposition review ou suppression auto).',
    rationale:
      'CyFun PR.DS-3.1 (Basic) attend que les actifs et media soient elimines de manière sure. Sans politique, les données restent indefiniment ou sont supprimees sans trace.',
    severity: 'medium',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(c)', label: 'Continuite + gestion des sauvegardes' },
      { framework: 'ISO27001',  ref: 'A.5.34',        label: 'Confidentialite et protection des données' },
      { framework: 'CyFun',     ref: 'PR.DS-3',       label: 'Asset disposal (PR.DS-3.1 = elimination sure)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/beta/security/labels/retentionLabels'],
    remediation:
      'Microsoft Purview > Data Lifecycle Management > Labels. Créer des retention labels avec retentionDuration + actionAfterRetention=startDispositionReview pour les données critiques.',
    manual: false,
  },
  {
    id: 'DP-009',
    family: 'data',
    title: 'Backup / preservation des données critiques',
    description:
      'Le tenant dispose d au moins 2 mecanismes de preservation des données critiques : retention labels, Exchange archiving, MIP E5, ou M365 Backup.',
    rationale:
      'CyFun PR.IP-4.1 (Basic) attend des backups stockes hors du système primaire. Dans M365, M365 Backup (nouvelle offre 2024+) ou Exchange Archiving + retention labels constituent la réponse moderne.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(c)', label: 'Gestion des sauvegardes et reprise' },
      { framework: 'ISO27001',  ref: 'A.8.13',        label: 'Sauvegarde des informations' },
      { framework: 'CIS-M365',  ref: '6.x',           label: 'Backups / litigation hold for critical mailboxes' },
      { framework: 'CyFun',     ref: 'PR.IP-4',       label: 'Backups maintained (PR.IP-4.1 = données critiques)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/beta/security/labels/retentionLabels', '/subscribedSkus'],
    remediation:
      'Souscrire a Microsoft 365 Backup (recommande). Sinon : activer Litigation Hold sur les mailboxes critiques + retention labels Exchange/SPO/OneDrive avec retention >= 7 ans selon obligations legales.',
    manual: false,
  },
  {
    id: 'LG-006',
    family: 'logs',
    title: 'Microsoft Secure Score surveille',
    description:
      'Le Secure Score Microsoft est >= 50 % et calcule il y a moins de 7 jours.',
    rationale:
      'CyFun ID.RA-5.1 (Basic) attend une évaluation des risques basee sur menaces/vulnerabilites/impacts. Le Secure Score est l outil natif d évaluation continue, mis a jour quotidiennement.',
    severity: 'medium',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(a)', label: 'Politique d analyse des risques' },
      { framework: 'ISO27001',  ref: 'A.5.7',         label: 'Renseignement sur les menaces' },
      { framework: 'CyFun',     ref: 'ID.RA-5',       label: 'Risk assessments (ID.RA-5.1 = threats/vulnerabilities/impact)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/security/secureScores'],
    remediation:
      'Microsoft 365 Defender > Secure Score. Appliquer les recommandations a fort impact en priorite. Cibler 60% minimum, 80% pour orgs matures.',
    manual: false,
  },
  {
    id: 'LG-007',
    family: 'logs',
    title: 'Defender for Endpoint licencie et deploye',
    description:
      'Le tenant dispose de licences Defender for Endpoint (P1/P2 ou Defender Business) avec une couverture utilisateur >= 80%.',
    rationale:
      'CyFun DE.CM-3.1 (Basic) attend des outils de monitoring endpoint detectant les comportements suspects. Defender for Endpoint (EDR) est l outil natif Microsoft pour ce besoin.',
    severity: 'high',
    references: [
      { framework: 'NIS2',      ref: 'Art. 21(2)(j)', label: 'Cyber-hygiene + outils détection' },
      { framework: 'ISO27001',  ref: 'A.8.7',         label: 'Protection contre les malware' },
      { framework: 'CIS-M365',  ref: '5.x',           label: 'EDR/MDR deployed on endpoints' },
      { framework: 'CyFun',     ref: 'DE.CM-3',       label: 'Endpoint behaviour monitored (DE.CM-3.1 = EDR)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/subscribedSkus'],
    remediation:
      'Souscrire a Microsoft Defender for Endpoint P1/P2 (E5/E5 Security) ou Defender Business. Onboarder tous les endpoints via Intune ou GPO + script onboarding.',
    manual: false,
  },

  // ===========================================================================
  // CyFun Important gap closure (Phase B - 2026.06.2)
  // 26 nouveaux contrôles vers la couverture niveau Important.
  // ===========================================================================
  { id: 'ID-014', family: 'identity', title: 'Authentication Strength sur les admins', severity: 'critical',
    description: 'Au moins une Conditional Access Policy applique une authenticationStrength (FIDO2, Phishing-resistant) sur les roles administrateurs.',
    rationale: 'Les comptes admins sont les cibles privilegiees. CyFun PR.AC-1.4 (Important) exige MFA fort.',
    references: [
      { framework: 'NIS2', ref: 'Art. 21(2)(j)', label: 'MFA renforcee' },
      { framework: 'CyFun', ref: 'PR.AC-1', label: 'Identités gérés (PR.AC-1.4 = MFA strong)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation: 'CA Policy : Users=privileged role members / Grant=Require authentication strength = "Phishing-resistant MFA".',
    manual: false },

  { id: 'ID-015', family: 'identity', title: 'Identity Protection - blocage signInRisk', severity: 'high',
    description: 'Une CA Policy bloque ou exige MFA sur les sign-ins risques (haut/moyen).',
    rationale: 'Identity Protection (P2) detecte les sessions risquees. Sans policy, les détections restent passives.',
    references: [
      { framework: 'CyFun', ref: 'DE.CM-7', label: 'Monitoring accès non autorises', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation: 'CA Policy : signInRiskLevels=[high,medium] / Grant=Block. Requires Entra ID P2.',
    manual: false },

  { id: 'ID-016', family: 'identity', title: 'Identity Protection - blocage userRisk', severity: 'high',
    description: 'Une CA Policy force password change ou blocage sur les users compromis.',
    rationale: 'Les users avec credentials leaked (dark web monitoring) doivent être forces a regenerer.',
    references: [
      { framework: 'CyFun', ref: 'DE.CM-7', label: 'Unauthorized users monitored (PR.AC-1.5)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation: 'CA Policy : userRiskLevels=[high] / Grant=Require password change. Requires Entra ID P2.',
    manual: false },

  { id: 'ID-017', family: 'identity', title: 'Device Code Flow bloque', severity: 'high',
    description: 'Une CA Policy bloque le Device Code Flow (sauf exceptions controlees).',
    rationale: 'Le Device Code Flow est massivement utilise dans le phishing AiTM (Adversary-in-the-Middle) en 2025-2026.',
    references: [
      { framework: 'CyFun', ref: 'PR.AC-7', label: 'Auth proportionnée au risque', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation: 'CA Policy : Conditions > Authentication flows > Device code flow / Grant=Block. Exempter eventuellement un groupe spécifique (kiosques).',
    manual: false },

  { id: 'ID-018', family: 'identity', title: 'Accès aux apps critiques limite aux devices compliant', severity: 'high',
    description: 'Au moins une CA Policy exige compliantDevice ou domainJoinedDevice.',
    rationale: 'Limite l accès aux endpoints gérés par l organisation, réduit l exposition aux BYOD non contrôles.',
    references: [
      { framework: 'CyFun', ref: 'PR.AC-3', label: 'Remote access (PR.AC-3.5 managed devices)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation: 'CA Policy : Cloud apps=All / Grant=Require compliant device (Intune). Nécessite Intune.',
    manual: false },

  { id: 'ID-019', family: 'identity', title: 'Sign-in frequency configuree sur admins', severity: 'medium',
    description: 'Une CA Policy impose la re-authentification régulière des admins (sign-in frequency).',
    rationale: 'Réduit le risque de session hijacking sur les comptes a privileges.',
    references: [
      { framework: 'CyFun', ref: 'PR.AC-7', label: 'Sessions terminate after inactivity (PR.AC-7.4)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation: 'CA Policy : roles admin / Session controls > Sign-in frequency = 4 hours. "Every time" pour M365 Admin Center.',
    manual: false },

  { id: 'ID-020', family: 'identity', title: 'Restrictions invitations guests (B2B)', severity: 'medium',
    description: 'Les invitations sont restreintes aux admins et le guest role est limite (pas Member-like).',
    rationale: 'Sans restriction, n importe quel user peut inviter des externes a acceder a SharePoint/Teams.',
    references: [
      { framework: 'CyFun', ref: 'PR.AC-4', label: 'External access controlled', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/policies/authorizationPolicy'],
    remediation: 'Entra > External Identities > Guest invite restrictions = Specific admin roles + Guest user access = Restricted.',
    manual: false },

  { id: 'ID-021', family: 'identity', title: 'App Registrations restreintes', severity: 'high',
    description: 'Les utilisateurs standards ne peuvent pas enregistrer d application dans Entra.',
    rationale: 'Bloque les apps OAuth phishing creees par compte compromis.',
    references: [
      { framework: 'CyFun', ref: 'PR.AC-4', label: 'Authorized users only register apps', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/policies/authorizationPolicy'],
    remediation: 'Entra > Users > User settings > App registrations = "No".',
    manual: false },

  { id: 'ID-022', family: 'identity', title: 'Consentement utilisateur aux apps bloque', severity: 'high',
    description: 'Le user consent aux apps tierces est bloque et un Admin Consent Workflow est en place.',
    rationale: 'Empeche les OAuth phishing (le pattern d attaque ANSSI #2024-DEF).',
    references: [
      { framework: 'CyFun', ref: 'PR.AC-1', label: 'Illicit consent grants prevented', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/policies/authorizationPolicy', '/policies/adminConsentRequestPolicy'],
    remediation: 'Entra > Enterprise apps > User consent = "Do not allow" + activer Admin consent workflow.',
    manual: false },

  { id: 'ID-023', family: 'identity', title: 'Password Protection (banned list + smart lockout)', severity: 'medium',
    description: 'Custom banned password list contient au moins 10 mots et Smart Lockout est active (<=10 tentatives).',
    rationale: 'Bloque les patterns de mots de passe communs (entreprise, produits, saisons).',
    references: [
      { framework: 'CyFun', ref: 'PR.AC-1', label: 'Passwords protect against common attacks', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/directorySettings'],
    remediation: 'Entra > Authentication methods > Password protection. Banned list custom + Smart Lockout threshold = 10.',
    manual: false },

  { id: 'ID-024', family: 'identity', title: 'Comptes break-glass configures', severity: 'critical',
    description: 'Au moins 2 comptes Global Admin sont exclus des CA Policies MFA (= break-glass) pour éviter le lockout.',
    rationale: 'En cas de panne MFA (provider down), un compte break-glass garantit la reprise de contrôle.',
    references: [
      { framework: 'CyFun', ref: 'PR.IP-11', label: 'Emergency access procédures', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/roleManagement/directory/roleAssignments', '/identity/conditionalAccess/policies'],
    remediation: 'Créer 2 comptes GA, exclus des CA Policies, password fort 20+ chars en coffre offline. Audit les sign-ins via Sentinel.',
    manual: false },

  { id: 'EM-007', family: 'email', title: 'MTA-STS strict sur tous les domaines', severity: 'medium',
    description: 'Tous les domaines verifies publient un record MTA-STS avec policy mode=enforce.',
    rationale: 'Force la connexion TLS entrante et bloque les downgrade attacks.',
    references: [
      { framework: 'CyFun', ref: 'PR.AC-1', label: 'Email transit encryption hardened', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/domains'],
    remediation: 'Publier mta-sts.<domain> + TXT _mta-sts.<domain> + policy file mode=enforce. Activer TLSRPT.',
    manual: false },

  { id: 'EM-008', family: 'email', title: 'Tenant Allow/Block List gérée', severity: 'medium',
    description: 'Defender Tenant Allow/Block List est review trimestriellement avec des entrees Block sur IoCs.',
    rationale: 'Bloque rapidement les IoCs identifies par threat intel ou incidents passes.',
    references: [
      { framework: 'CyFun', ref: 'PR.DS-2', label: 'Email IoCs blocked', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Defender > Policies > Tenant Allow/Block Lists. Review trimestrielle + intégrer threat intel feeds.',
    manual: true },

  { id: 'EM-009', family: 'email', title: 'Quarantine policies personnalisees', severity: 'medium',
    description: 'Des quarantine policies personnalisees existent avec notifications utilisateur + workflow release admin.',
    rationale: 'Les quarantine policies default n alertent pas les end-users, ce qui ralentit la détection.',
    references: [
      { framework: 'CyFun', ref: 'PR.DS-2', label: 'Quarantined items reviewed', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Defender > Quarantine policies. Notification daily + Release par admin pour high-severity.',
    manual: true },

  { id: 'EM-010', family: 'email', title: 'Priority Account Protection active', severity: 'high',
    description: 'Les comptes VIP (C-level, finance) sont designes en Priority Accounts et beneficient du Strict preset.',
    rationale: 'Les comptes VIP sont la cible #1 du BEC (Business Email Compromise).',
    references: [
      { framework: 'CyFun', ref: 'PR.AC-4', label: 'Privileged accounts protected', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Defender > Settings > Priority account protection > Add priority accounts. Strict preset + impersonation protection.',
    manual: true },

  { id: 'EM-011', family: 'email', title: 'Outbound spam limits hardened', severity: 'medium',
    description: 'Limites strictes sur les envois externes + auto-forwarding desactive.',
    rationale: 'Detecte rapidement les comptes compromis utilisés pour spam ou exfiltration.',
    references: [
      { framework: 'CyFun', ref: 'DE.CM-7', label: 'Outbound unusual traffic monitored', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Defender > Outbound spam policy. ExternalRecipientPerHour=500, automatic forwarding=Off.',
    manual: true },

  { id: 'DP-010', family: 'data', title: 'Sensitivity labels avec encryption', severity: 'high',
    description: 'Au moins un sensitivity label applique l encryption (Confidential / Strictly Confidential).',
    rationale: 'Sans encryption, le label est uniquement visuel et n offre aucune protection cryptographique.',
    references: [
      { framework: 'CyFun', ref: 'PR.DS-1', label: 'Data at rest encrypted via labels', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/beta/security/informationProtection/sensitivityLabels'],
    remediation: 'Purview > Labels > Confidential > Encryption: ON + Watermark + Content marking.',
    manual: false },

  { id: 'DP-011', family: 'data', title: 'Auto-labeling actif', severity: 'medium',
    description: 'Des policies d auto-labeling sont actives sur Exchange + SharePoint avec sensitive info types (PII UE, IBAN).',
    rationale: 'L auto-labeling réduit la dependance a l action utilisateur, et homogeneise la classification.',
    references: [
      { framework: 'CyFun', ref: 'PR.DS-5', label: 'Data labeled automatically', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Purview > Auto-labeling > Create policy. Mode Simulate puis Enforce.',
    manual: true },

  { id: 'DP-012', family: 'data', title: 'DLP cible le partage externe', severity: 'high',
    description: 'Au moins une DLP policy cible le partage externe avec actions Block/Notify.',
    rationale: 'DLP externe est la 1ere ligne de defense contre les fuites données clients/financieres.',
    references: [
      { framework: 'CyFun', ref: 'PR.DS-5', label: 'External data sharing controlled', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Purview DLP > Locations=SPO+OneDrive+Teams / Conditions=shared external + SIT EU PII / Action=Restrict access + Notify.',
    manual: true },

  { id: 'DP-013', family: 'data', title: 'Customer Lockbox active', severity: 'medium',
    description: 'Customer Lockbox est ON : Microsoft requiert une approbation client pour acceder aux données.',
    rationale: 'Souverainete : Microsoft ne peut acceder a vos données qu avec votre accord explicite.',
    references: [
      { framework: 'CyFun', ref: 'PR.DS-5', label: 'Microsoft access requires approval', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'M365 Admin > Settings > Security & privacy > Customer Lockbox = ON. Nécessite E5 ou Compliance E5.',
    manual: true },

  { id: 'DP-014', family: 'data', title: 'EU Data Boundary (residency)', severity: 'medium',
    description: 'Le tenant est cree en EU (countryLetterCode) pour beneficier de l EU Data Boundary.',
    rationale: 'NIS2 + RGPD imposent souvent une residency EU pour les données personnelles UE.',
    references: [
      { framework: 'NIS2', ref: 'Art. 21(2)(g)', label: 'Cryptographie + chiffrement' },
      { framework: 'CyFun', ref: 'PR.DS-1', label: 'Data residency controlled', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/organization'],
    remediation: 'Si tenant cree hors EU : évaluer Multi-Geo (E5) pour residency par mailbox/OneDrive. Sinon, accepter la limitation.',
    manual: false },

  { id: 'DP-015', family: 'data', title: 'Insider Risk Management actif', severity: 'medium',
    description: 'Au moins une policy Insider Risk est active (data theft, leaks, departing users).',
    rationale: 'Detecte les comportements anomaux des collaborateurs (exfiltration avant depart, telechargements massifs).',
    references: [
      { framework: 'CyFun', ref: 'DE.CM-3', label: 'Insider threats monitored', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Purview > Insider Risk Management > Templates. Nécessite E5 Compliance.',
    manual: true },

  { id: 'LG-008', family: 'logs', title: 'Alert policies actives (>= 30)', severity: 'medium',
    description: 'Au moins 30 alert policies sont actives, dont des customs sur Forwarding rules, External transfers.',
    rationale: 'Les defaults Microsoft sont un point de depart, mais les vraies attaques recentes (2024-2026) demandent des customs.',
    references: [
      { framework: 'CyFun', ref: 'DE.AE-1', label: 'Baseline alerts configured', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Defender > Policies > Alert policies. Garder defaults ON + ajouter customs Forwarding rule, OAuth permissions.',
    manual: true },

  { id: 'LG-009', family: 'logs', title: 'Microsoft Sentinel connecte', severity: 'high',
    description: 'Un workspace Sentinel existe avec data connectors actifs sur M365 (O365, Entra, Defender XDR).',
    rationale: 'Sentinel est le SIEM Microsoft, indispensable pour analyse correlative cross-source.',
    references: [
      { framework: 'CyFun', ref: 'DE.AE-3', label: 'Events aggregated for analysis', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Azure Portal > Sentinel > Data connectors > Office 365 + Entra ID Sign-in + Audit + Defender XDR. Couts variables.',
    manual: true },

  { id: 'LG-010', family: 'logs', title: 'Defender XDR integrations unifiees', severity: 'high',
    description: 'Au moins 2 composants Defender (MDE / MDO P2 / MDA) sont actifs et alertes recentes existent.',
    rationale: 'Sans XDR, les détections restent silotees et la correlation manquante.',
    references: [
      { framework: 'CyFun', ref: 'DE.AE-2', label: 'Anomalies correlated', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/security/alerts_v2', '/subscribedSkus'],
    remediation: 'Unifier MDE + MDO P2 + MDA dans security.microsoft.com. Activer Auto Investigation & Response (AIR).',
    manual: false },

  { id: 'LG-011', family: 'logs', title: 'Advanced Hunting custom détections', severity: 'medium',
    description: 'Au moins 5 détections custom KQL sont actives dans Defender XDR.',
    rationale: 'Le threat hunting proactif est requis par CyFun Important.',
    references: [
      { framework: 'CyFun', ref: 'DE.AE-2', label: 'Threat hunting performed', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Defender > Hunting > Custom détection rules. Importer du repo Microsoft/Microsoft-365-Defender-Hunting-Queries.',
    manual: true },

  // --- 8 contrôles supplémentaires (06/2026) - 69 contrôles total ---
  { id: 'ID-025', family: 'identity', title: 'Tenant Restrictions v2 actives', severity: 'high',
    description: 'Cross-tenant access policy bloque par défaut + allow-list spécifique.',
    rationale: 'Empêche l exfiltration vers tenants tiers non autorisés (data leak via apps cross-tenant).',
    references: [{ framework: 'CyFun', ref: 'PR.AC-4', label: 'Cross-tenant access controlled', cyfunLevel: 'Important' }],
    graphEndpoints: ['/policies/crossTenantAccessPolicy/default'],
    remediation: 'Entra > External Identities > Cross-tenant access > Default settings > Tenant restrictions = Block all + allow specific.',
    manual: false },

  { id: 'ID-026', family: 'identity', title: 'Workload Identities monitorées', severity: 'medium',
    description: 'Aucune credentials d App Registration expirée + < 3 expirent dans 30 jours.',
    rationale: 'Service Principals avec secrets expirés ou non rotés sont une faille critique (CVE 2024-1709 catégorie).',
    references: [{ framework: 'CyFun', ref: 'DE.CM-7', label: 'Non-human accounts monitored', cyfunLevel: 'Important' }],
    graphEndpoints: ['/applications'],
    remediation: 'Audit trimestriel : roter secrets, supprimer apps non utilisées, remplacer par certificats ou federated credentials.',
    manual: false },

  // ===========================================================================
  // CONDITIONAL ACCESS AVANCE (v1.2 - CAE / BlockDownload / AuthContexts)
  // ===========================================================================
  { id: 'ID-027', family: 'identity', title: 'Continuous Access Évaluation (CAE)', severity: 'high',
    description: 'Continuous Access Évaluation est activée pour toutes les apps supportées (Exchange Online, SharePoint, Teams, Graph). Les tokens d\'accès sont révoqués en quasi-temps réel en cas de risque détecté.',
    rationale: 'Sans CAE, un token compromis reste valide jusqu\'à 1h. Avec CAE, la révocation est effective en <5 min. Critique pour réduire la fenêtre d\'exploitation post-compromission.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)',  label: 'Gestion des sessions d\'authentification' },
      { framework: 'ISO27001', ref: 'A.5.16',         label: 'Gestion de l\'identité' },
      { framework: 'CIS-M365', ref: '1.3.4',          label: 'CAE enabled' },
      { framework: 'CyFun',    ref: 'PR.AC-7',        label: 'Session revocation on risk', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation: 'Entra > Conditional Access > Settings > Continuous access évaluation = Enable for all. Note : nécessite Entra ID P1.',
    manual: false },

  { id: 'ID-028', family: 'identity', title: 'Blocage download depuis devices non gérés (SPO/OneDrive)', severity: 'medium',
    description: 'Une Conditional Access Policy bloque le download (ou applique web-only restrictions) sur SharePoint et OneDrive quand l\'accès vient d\'un device non Intune-compliant / non Entra-joined.',
    rationale: 'Empêche l\'exfiltration de données vers BYOD ou postes personnels. Le user peut consulter en ligne mais pas télécharger ni synchroniser.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Protection des données contre l\'exfiltration' },
      { framework: 'ISO27001', ref: 'A.8.12',        label: 'Data leakage prevention' },
      { framework: 'CIS-M365', ref: '2.6.2',         label: 'Block download on unmanaged devices' },
      { framework: 'CyFun',    ref: 'PR.DS-5',       label: 'Data exfiltration controlled', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation: 'Créer CA Policy : Users=All / Cloud apps=SharePoint+OneDrive / Conditions=Device platform=Any / Session=Use Conditional Access App Control "Block downloads".',
    manual: false },

  { id: 'ID-029', family: 'identity', title: 'Authentication Contexts pour apps sensibles', severity: 'medium',
    description: 'Au moins un Authentication Context est défini et utilisé (par exemple sur SharePoint sites confidentiels ou apps métier critiques) pour exiger un step-up MFA / device-compliant lors de l\'accès à des ressources spécifiques.',
    rationale: 'Plus fin que les CA Policies globales. Permet d\'exiger un niveau de garantie supplémentaire sur des ressources précises (SPO docs Confidentiel, apps tier-0) sans pénaliser l\'usage quotidien.',
    references: [
      { framework: 'ISO27001', ref: 'A.5.15',  label: 'Contrôle d\'accès' },
      { framework: 'CIS-M365', ref: '1.3.7',   label: 'Authentication contexts in use' },
      { framework: 'CyFun',    ref: 'PR.AC-4', label: 'Step-up authentication for sensitive resources', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/authenticationContextClassReferences'],
    remediation: 'Entra > Conditional Access > Authentication contexts > New > c1=Step-up MFA. Puis SPO Admin > Site > Sensitivity = c1 OU CA Policy > Cloud apps = "Authentication context" > c1.',
    manual: false },

  // ===========================================================================
  // ENDPOINT & DEVICES (v1.2 - nouvelle famille Intune / MDE)
  // ===========================================================================
  { id: 'ENP-001', family: 'endpoint', title: 'Politique de conformité Intune active', severity: 'high',
    description: 'Au moins une compliance policy Intune est active et appliquée (devices Windows/macOS au minimum).',
    rationale: 'Sans compliance policy, les CA Policies "Require compliant device" ne servent à rien (tous les devices sont marqués compliant=null).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Sécurité des systèmes d\'information' },
      { framework: 'ISO27001', ref: 'A.8.1',         label: 'Devices endpoint' },
      { framework: 'CIS-M365', ref: '8.1.1',         label: 'Intune compliance policy' },
      { framework: 'CyFun',    ref: 'PR.IP-1',       label: 'Baseline configuration endpoint', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/deviceManagement/deviceCompliancePolicies'],
    remediation: 'Intune > Devices > Compliance policies > Create > Windows 10/11 > BitLocker + Secure Boot + Defender AV + min OS version. Assigner à All devices.',
    manual: false },

  { id: 'ENP-002', family: 'endpoint', title: 'Microsoft Defender Antivirus configuré', severity: 'critical',
    description: 'Une configuration profile Intune (Endpoint security > Antivirus) impose Defender AV avec Cloud Protection + Real-time + Tamper Protection actifs.',
    rationale: 'Defender AV est le rempart de base. Sans Tamper Protection, un malware peut désactiver Defender sur le device.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Anti-malware' },
      { framework: 'ISO27001', ref: 'A.8.7',         label: 'Protection contre les malwares' },
      { framework: 'CIS-M365', ref: '8.1.2',         label: 'Defender AV configured via Intune' },
      { framework: 'CyFun',    ref: 'DE.CM-4',       label: 'Malicious code detected', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/deviceManagement/configurationPolicies'],
    remediation: 'Intune > Endpoint security > Antivirus > Create policy > Windows > Microsoft Defender Antivirus > Real-time=On, Cloud Protection=Block at first sight, Tamper Protection=On.',
    manual: false },

  { id: 'ENP-003', family: 'endpoint', title: 'Attack Surface Reduction rules en mode Block', severity: 'high',
    description: 'Au moins 8 des 15 règles ASR Defender sont configurées en mode Block (vs Audit ou Off). Couvre la majorité des techniques d\'exploit moderne (Office macros, Office subprocesses, exec from email, etc.).',
    rationale: 'Les ASR rules bloquent les techniques d\'attaque les plus exploitées (Living off the Land, droppers, etc.). Microsoft les recommande comme couche essentielle sans coût.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Hardening endpoint' },
      { framework: 'ISO27001', ref: 'A.8.7',         label: 'Protection contre les malwares' },
      { framework: 'CIS-M365', ref: '8.5.1',         label: 'ASR rules in Block mode' },
      { framework: 'CyFun',    ref: 'PR.IP-1',       label: 'Endpoint hardening baseline', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/deviceManagement/configurationPolicies'],
    remediation: 'Intune > Endpoint security > Attack surface reduction > Create policy > Attack surface reduction rules > Enable Block mode pour : Office macros, Office subprocesses, exec from email, Office injection, scripts unsigned, etc.',
    manual: false },

  { id: 'ENP-004', family: 'endpoint', title: 'BitLocker enforcement via Intune', severity: 'high',
    description: 'Une configuration profile Intune force BitLocker XTS-AES 256 sur les drives système + fixed + removable des devices Windows 10/11.',
    rationale: 'Empêche l\'exfiltration de données via vol/perte du device. Microsoft Bitlocker recovery keys stockées dans Entra ID pour récupération.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Chiffrement des données au repos' },
      { framework: 'ISO27001', ref: 'A.8.24',        label: 'Utilisation de la cryptographie' },
      { framework: 'CIS-M365', ref: '8.4.1',         label: 'BitLocker enforced via Intune' },
      { framework: 'CyFun',    ref: 'PR.DS-1',       label: 'Data-at-rest protected', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/deviceManagement/configurationPolicies'],
    remediation: 'Intune > Endpoint security > Disk encryption > Create policy > Windows > BitLocker > Encryption algorithm = XTS-AES 256, Recovery key save to Entra ID.',
    manual: false },

  { id: 'ENP-005', family: 'endpoint', title: 'App Protection Policies (MAM) sur mobiles', severity: 'medium',
    description: 'Des App Protection Policies Intune sont assignées pour iOS et Android couvrant les apps Microsoft (Outlook, Teams, Word, Excel, OneDrive) : PIN requis, copy-paste restreint, sauvegarde cloud bloquée.',
    rationale: 'Permet le BYOD sécurisé sans enrôlement complet (MDM). Protège les données M365 même sur device personnel non géré.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Protection des données mobiles' },
      { framework: 'ISO27001', ref: 'A.6.7',         label: 'Travail à distance' },
      { framework: 'CIS-M365', ref: '8.6.1',         label: 'App Protection Policies for mobile' },
      { framework: 'CyFun',    ref: 'PR.AC-3',       label: 'Mobile devices access controlled', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/deviceAppManagement/managedAppPolicies'],
    remediation: 'Intune > Apps > App protection policies > Create > iOS+Android > Public Apps = Microsoft (Outlook, Teams, Office, OneDrive) > Settings = PIN required + No cloud backup + Restrict copy-paste.',
    manual: false },

  { id: 'ENP-006', family: 'endpoint', title: 'Devices Windows à jour (Quality + Feature updates)', severity: 'high',
    description: '> 90% des devices Windows sont à jour : pas de retard de quality update > 30 jours, OS version dans la fenêtre supportée Microsoft.',
    rationale: 'Patches critiques publiés mensuellement (Patch Tuesday). Retard = exposition aux CVE déjà patchées.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Gestion des vulnérabilités' },
      { framework: 'ISO27001', ref: 'A.8.8',         label: 'Gestion des vulnérabilités techniques' },
      { framework: 'CIS-M365', ref: '8.3.1',         label: 'Update compliance >= 90%' },
      { framework: 'CyFun',    ref: 'PR.IP-12',      label: 'Vulnerabilities patched', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/deviceManagement/managedDevices'],
    remediation: 'Intune > Devices > Windows > Update rings > Create > General Availability Channel + Pause deferral period 0-7 days. Reports > Windows update.',
    manual: false },

  { id: 'ENP-007', family: 'endpoint', title: 'Defender for Endpoint onboarding des devices', severity: 'high',
    description: '> 85% des devices Windows gérés par Intune sont onboardés sur Microsoft Defender for Endpoint (sensor actif et reporting).',
    rationale: 'MDE est l\'EDR Microsoft. Sans onboarding, pas de détection avancée ni de Threat & Vulnerability Management.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(f)', label: 'Détection des incidents' },
      { framework: 'ISO27001', ref: 'A.8.16',        label: 'Surveillance des activités' },
      { framework: 'CIS-M365', ref: '8.5.3',         label: 'MDE deployment >= 85%' },
      { framework: 'CyFun',    ref: 'DE.CM-7',       label: 'Endpoint monitoring active', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/deviceManagement/managedDevices'],
    remediation: 'Intune > Endpoint security > Endpoint détection and response > Create policy > Microsoft Defender for Endpoint > Auto-onboard. Vérifier dans Defender XDR > Device inventory.',
    manual: false },

  { id: 'ENP-008', family: 'endpoint', title: 'Autopilot enrollment + Hello for Business', severity: 'medium',
    description: 'Les devices Windows sont enrôlés via Autopilot (zero-touch) et Windows Hello for Business est activé (passwordless / biométrie).',
    rationale: 'Réduit la dépendance aux mots de passe et standardise le provisioning. Compatible CyFun PR.AC-1.4 (phishing-resistant auth).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)',  label: 'Authentification forte' },
      { framework: 'ISO27001', ref: 'A.8.5',          label: 'Authentification sécurisée' },
      { framework: 'CIS-M365', ref: '8.2.1',          label: 'Hello for Business + Autopilot' },
      { framework: 'CyFun',    ref: 'PR.AC-1',        label: 'Phishing-resistant authentication on endpoints', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: ['/deviceManagement/windowsAutopilotDeviceIdentities'],
    remediation: 'Intune > Devices > Windows > Windows enrollment > Autopilot > Profiles + Hello for Business policy > Use Hello = Enabled, Use security keys = Enabled.',
    manual: false },

  // ===========================================================================
  // COLLABORATION TEAMS (v1.2 - nouvelle famille)
  // ===========================================================================
  { id: 'COL-001', family: 'collab', title: 'Teams External Access restreint à des domaines spécifiques', severity: 'high',
    description: 'L\'accès externe Teams (federation chat/call) est limité à une allowlist de domaines partenaires, OU explicitement désactivé pour les tenants/SIP fédérés. Pas de "Allow all external domains".',
    rationale: 'Sans restriction, un attaquant qui crée un tenant peut chatter avec vos employés (BEC via Teams). 2024 = explosion de phishing Teams via fédération ouverte.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Sécurité des communications' },
      { framework: 'ISO27001', ref: 'A.5.14',        label: 'Transfert d\'informations' },
      { framework: 'CIS-M365', ref: '6.1.1',         label: 'Teams external access restricted' },
      { framework: 'CyFun',    ref: 'PR.AC-4',       label: 'Federation controlled', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/policies/crossTenantAccessPolicy/default'],
    remediation: 'Teams Admin > Users > External access > "Allow only specific external domains" + ajout des domaines partenaires. OU "Block all external domains".',
    manual: false },

  { id: 'COL-002', family: 'collab', title: 'Federation Skype for Business (XMPP/SIP) désactivée', severity: 'medium',
    description: 'La federation legacy avec Skype for Business consumer/on-prem est désactivée si non utilisée. Réduit la surface d\'attaque (SIP, XMPP).',
    rationale: 'La fédération Skype consumer est encore activée par défaut sur beaucoup de tenants et n\'est presque jamais utilisée. Vecteur d\'attaque négligé.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Hardening communications' },
      { framework: 'ISO27001', ref: 'A.8.20',        label: 'Sécurité des réseaux' },
      { framework: 'CIS-M365', ref: '6.1.2',         label: 'Skype federation disabled' },
      { framework: 'CyFun',    ref: 'PR.AC-4',       label: 'Legacy federation disabled', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: [],
    remediation: 'Teams Admin > External access > Skype users = OFF. Vérification via Get-CsTenantFederationConfiguration > AllowPublicUsers = $false.',
    manual: true },

  { id: 'COL-003', family: 'collab', title: 'Meeting policy : Anonymous join désactivé ou restreint', severity: 'high',
    description: 'La meeting policy globale (ou des policies dédiées aux groupes sensibles) interdit le rejoin anonyme aux réunions Teams, OU exige lobby pour anonymous + dial-in.',
    rationale: 'Anonymous join activé sans lobby = quiconque avec le lien rejoint la réunion. Risque fuite confidentielle + bombing (zoombombing-like).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Confidentialité des réunions' },
      { framework: 'ISO27001', ref: 'A.5.14',        label: 'Transfert d\'informations' },
      { framework: 'CIS-M365', ref: '6.3.1',         label: 'Anonymous meeting join restricted' },
      { framework: 'CyFun',    ref: 'PR.AC-3',       label: 'Meeting access controlled', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Teams Admin > Meetings > Meeting policies > Global > "Anonymous users can join a meeting" = Off OU "Who can bypass lobby" = "People in my org".',
    manual: true },

  { id: 'COL-004', family: 'collab', title: 'App permission policy : restriction des apps tierces', severity: 'medium',
    description: 'Une app permission policy active limite les apps Teams installables (vs Allow all). Surtout : block apps inconnues / partenaires non vérifiés + blocage apps spécifiques connues pour data exfiltration.',
    rationale: 'Sans restriction, les users installent n\'importe quelle app tierce Teams qui peut accéder aux conversations/canaux. Risque exfiltration de données business.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Sécurité de l\'acquisition' },
      { framework: 'ISO27001', ref: 'A.8.30',        label: 'Externalisation et développement' },
      { framework: 'CIS-M365', ref: '6.5.1',         label: 'Teams app permission policy' },
      { framework: 'CyFun',    ref: 'PR.IP-1',       label: 'Third-party apps governance', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/teamwork/teamsAppSettings'],
    remediation: 'Teams Admin > Teams apps > Permission policies > Global > Third-party apps = "Block all" + Allow custom apps = Allow specific apps OU Block all.',
    manual: false },

  { id: 'COL-005', family: 'collab', title: 'Guest access Entra ID + Teams contrôlé', severity: 'medium',
    description: 'La policy Entra ID restreint la création d\'invitations guests aux admins (allowInvitesFrom != everyone), ET les guests Teams ont des permissions limitées (pas de création de canaux, etc.).',
    rationale: 'Permettre à tous les users d\'inviter des guests = porte ouverte aux comptes sponsors fictifs et compromissions. Standard CIS-M365.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(i)', label: 'Contrôle d\'accès' },
      { framework: 'ISO27001', ref: 'A.5.18',        label: 'Droits d\'accès' },
      { framework: 'CIS-M365', ref: '1.1.7',         label: 'Guest invite restrictions' },
      { framework: 'CyFun',    ref: 'PR.AC-4',       label: 'Guest access governed', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/policies/authorizationPolicy'],
    remediation: 'Entra Admin > External Identities > External collaboration settings > Guest invite settings = "Only users with specific admin roles can invite". Teams Admin > Guest access policies > Restrict guest channel/app/screen-share.',
    manual: false },

  // ===========================================================================
  // APPS & POWER PLATFORM (v1.2 - nouvelle famille apps)
  // ===========================================================================
  { id: 'APP-001', family: 'apps', title: 'Power Platform DLP policies pour connectors', severity: 'high',
    description: 'Au moins une politique DLP Power Platform classifie les connectors en Business / Non-business / Blocked. Empêche un Power Automate de pomper des données M365 vers un Gmail/Dropbox externe.',
    rationale: 'Sans DLP Power Platform, n\'importe quel citizen developer peut créer un flow qui exfiltre des données vers des connectors externes. Vecteur d\'exfiltration n°1 oublié.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Protection contre l\'exfiltration' },
      { framework: 'ISO27001', ref: 'A.8.12',        label: 'Data leakage prevention' },
      { framework: 'CIS-M365', ref: '7.1.1',         label: 'Power Platform DLP active' },
      { framework: 'CyFun',    ref: 'PR.DS-5',       label: 'Cross-system data flows controlled', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Power Platform Admin Center > Policies > Data Policies > New policy. Classer M365 connectors en Business, Twitter/Gmail/Dropbox en Blocked.',
    manual: true },

  { id: 'APP-002', family: 'apps', title: 'Power Platform - création d\'environnements restreinte', severity: 'medium',
    description: 'La création d\'environnements Power Platform (et Power Apps personnels) est restreinte aux admins. Les utilisateurs ne peuvent pas créer un environnement default-bypass.',
    rationale: 'Si les users peuvent créer des environnements à volonté, les DLP policies sont contournées (chaque env est gouverné séparément).',
    references: [
      { framework: 'ISO27001', ref: 'A.8.30',        label: 'Externalisation et développement' },
      { framework: 'CIS-M365', ref: '7.1.2',         label: 'Power Platform environment creation restricted' },
      { framework: 'CyFun',    ref: 'PR.IP-1',       label: 'Citizen developer governed', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Power Platform Admin Center > Settings > Tenant settings > Disable "Anyone can create environments" + restreindre Power Apps Plan trial.',
    manual: true },

  { id: 'APP-003', family: 'apps', title: 'Power Automate flows non-admin reviewed', severity: 'medium',
    description: 'Inventaire des flows Power Automate créés par des non-admins, et review trimestriel des flows qui touchent des données sensibles.',
    rationale: 'Citizen developers créent des flows légitimes, mais aussi des flows shadow qui survivent au départ de l\'employé. Risque opérationnel et sécurité.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Inventaire et gestion' },
      { framework: 'CIS-M365', ref: '7.1.3',         label: 'Power Automate flow review' },
      { framework: 'CyFun',    ref: 'DE.CM-7',       label: 'Non-human flows monitored', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: [],
    remediation: 'Power Platform Admin Center > Analytics > Power Automate > Flow runs. Setup CoE Starter Kit pour inventaire complet.',
    manual: true },

  { id: 'APP-004', family: 'apps', title: 'Service Principals & OAuth apps reviewed', severity: 'high',
    description: 'Inventaire des Service Principals et OAuth apps consentis sur le tenant. Aucune app avec permissions excessives non-utilisée (lurkers post-2024 attaque OAuth phishing).',
    rationale: 'Les attaques OAuth (consent phishing) installent des SP malveillants qui persistent. Audit trimestriel + suppression des SP non-utilisés.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Gestion des accès systèmes' },
      { framework: 'ISO27001', ref: 'A.5.16',        label: 'Gestion de l\'identité' },
      { framework: 'CIS-M365', ref: '7.1.4',         label: 'OAuth apps governance' },
      { framework: 'CyFun',    ref: 'DE.CM-7',       label: 'OAuth apps monitored', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/oauth2PermissionGrants', '/servicePrincipals'],
    remediation: 'Entra > Enterprise applications > Review > Filter "Last sign-in older than 90 days" → Désactiver. Activer "Admin consent workflow" pour les nouveaux requests.',
    manual: false },

  // ===========================================================================
  // MCAS / Defender for Cloud Apps (family logs - extension)
  // ===========================================================================
  { id: 'LG-015', family: 'logs', title: 'Defender for Cloud Apps - Office 365 connector', severity: 'medium',
    description: 'Le connector Microsoft 365 dans Defender for Cloud Apps est actif et reçoit les logs activités (Exchange, SharePoint, OneDrive, Teams, Power BI).',
    rationale: 'Sans le connector M365, MCAS n\'a pas la visibilité sur les activités cloud apps internes. Pré-requis fondamental pour Shadow IT discovery + activity policies.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(f)', label: 'Détection des incidents' },
      { framework: 'CIS-M365', ref: '9.1.1',         label: 'MCAS connector active' },
      { framework: 'CyFun',    ref: 'DE.CM-1',       label: 'Network activity monitored', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Defender XDR Portal > Settings > Cloud Apps > App connectors > Connect Microsoft 365 > Select all workloads.',
    manual: true },

  { id: 'LG-016', family: 'logs', title: 'Shadow IT Discovery via MCAS', severity: 'medium',
    description: 'Discovery Shadow IT est active via Defender for Cloud Apps (logs proxy/firewall uploadés, ou MDE Cloud App Catalog activé) avec review mensuelle des apps découvertes.',
    rationale: 'Identifie les 1000+ apps cloud non-sanctionnées que les utilisateurs accèdent. Critique pour gouvernance des données vers SaaS externes (NIS2 art. 21).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Inventaire SaaS' },
      { framework: 'CIS-M365', ref: '9.2.1',         label: 'Shadow IT discovery active' },
      { framework: 'CyFun',    ref: 'ID.AM-3',       label: 'External services inventoried', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Defender XDR Portal > Cloud Apps > Cloud Discovery > Upload logs OU enable MDE Cloud App Catalog. Setup snapshot reports + continuous reports.',
    manual: true },

  { id: 'LG-017', family: 'logs', title: 'OAuth Apps governance via MCAS', severity: 'high',
    description: 'Une App governance policy MCAS détecte les OAuth apps risquées (overprivileged, low-popularity, suspicious permissions) avec auto-revoke ou alerting sur les apps à risque.',
    rationale: 'Le pattern OAuth consent phishing est utilisé depuis 2020. MCAS App Governance est la défense moderne (au-delà du simple Entra Enterprise Applications review).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Sécurité de l\'authentification' },
      { framework: 'CIS-M365', ref: '9.3.1',         label: 'MCAS App Governance active' },
      { framework: 'CyFun',    ref: 'PR.AC-1',       label: 'OAuth apps controlled', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'Defender XDR > Cloud Apps > App Governance > Onboard + create policies "Risky apps" + "Apps with admin consent never used".',
    manual: true },

  { id: 'LG-018', family: 'logs', title: 'File policies MCAS pour données sensibles', severity: 'medium',
    description: 'Au moins une file policy MCAS détecte les fichiers contenant des données sensibles (PII UE, cartes de crédit) partagés en externe via SharePoint/OneDrive.',
    rationale: 'Complément DLP : MCAS scan les fichiers existants (pas juste les nouveaux flux) et déclenche des actions (notify owner, quarantine, remove sharing).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Détection des fuites' },
      { framework: 'ISO27001', ref: 'A.8.12',        label: 'Data leakage prevention' },
      { framework: 'CyFun',    ref: 'PR.DS-5',       label: 'Sensitive files monitored', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Defender XDR > Cloud Apps > Policies > File policy > Filter "sensitive content" + Action "Notify user + remove external sharing".',
    manual: true },

  { id: 'LG-019', family: 'logs', title: 'Anomaly détection policies MCAS active', severity: 'medium',
    description: 'Les Anomaly détection policies MCAS par défaut (Impossible Travel, Mass Download, Suspicious Activity from new IP, etc.) sont actives et alimentent les alertes Defender XDR.',
    rationale: 'Détection comportementale ML qui catche les incidents que CA Policies + DLP ratent. Souvent désactivé par défaut sur les tenants jeunes.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(f)', label: 'Détection comportementale' },
      { framework: 'CIS-M365', ref: '9.4.1',         label: 'MCAS anomaly détection active' },
      { framework: 'CyFun',    ref: 'DE.AE-1',       label: 'Baseline anomalies detected', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Defender XDR > Cloud Apps > Policies > Threat détection > Enable les anomaly détection policies par défaut.',
    manual: true },

  // ===========================================================================
  // BACKUP & RECOVERY M365 (family data - extension)
  // ===========================================================================
  { id: 'DP-018', family: 'data', title: 'Microsoft 365 Backup active', severity: 'high',
    description: 'Microsoft 365 Backup est activé sur Mailbox + SharePoint + OneDrive avec une rétention >= 1 an. Recovery rapide en cas d\'attaque ransomware ciblée M365.',
    rationale: 'NIS2 art. 21(2)(c) impose la continuité d\'activité. Les rétentions natives M365 (30j-1an selon licence) ne suffisent pas pour récupérer d\'un ransomware avec lateral mouvement vers SPO/OD.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(c)', label: 'Continuité d\'activité' },
      { framework: 'ISO27001', ref: 'A.8.13',        label: 'Sauvegarde des informations' },
      { framework: 'CIS-M365', ref: '6.6.1',         label: 'M365 Backup activated' },
      { framework: 'CyFun',    ref: 'PR.IP-4',       label: 'Backups tested', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'Admin Center > Setup > Microsoft 365 Backup > Onboard. OU solution tierce (Veeam, AvePoint, Commvault). Tester restore trimestriel.',
    manual: true },

  { id: 'DP-019', family: 'data', title: 'Litigation Hold sur boites mail VIP', severity: 'medium',
    description: 'Litigation Hold OU In-Place Hold est activé sur les boites mail des Global Admins, Executives, et Legal/HR (au minimum). Préserve les données malgré la suppression utilisateur.',
    rationale: 'Empêche un attaquant qui prend le contrôle d\'un compte VIP de couvrir ses traces en supprimant ses emails. Également requis pour eDiscovery légale.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(c)', label: 'Préservation des données' },
      { framework: 'ISO27001', ref: 'A.5.33',        label: 'Protection des registres' },
      { framework: 'CIS-M365', ref: '6.6.2',         label: 'Litigation hold on critical mailboxes' },
      { framework: 'CyFun',    ref: 'PR.IP-4',       label: 'Critical data preserved', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'EXO PowerShell : Get-Mailbox -RecipientTypeDetails UserMailbox | Where-Object {$_.Title -like "*Admin*"} | Set-Mailbox -LitigationHoldEnabled $true -LitigationHoldDuration 2555.',
    manual: true },

  { id: 'DP-020', family: 'data', title: 'Rétention long-terme pour archivage légal', severity: 'medium',
    description: 'Au moins une retention policy Purview avec rétention >= 7 ans est active sur Mailbox + SharePoint pour les départements concernés (Finance, Legal, HR).',
    rationale: 'Obligations légales sectorielles (conservation 7-10 ans pour Finance/Pharma/Banque). Doit être configuré côté Purview, pas juste M365 Backup.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(c)', label: 'Rétention réglementaire' },
      { framework: 'ISO27001', ref: 'A.5.33',        label: 'Protection des registres' },
      { framework: 'CyFun',    ref: 'PR.IP-4',       label: 'Long-term retention policies', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/security/labels/retentionLabels'],
    remediation: 'Purview Compliance Center > Records Management > Retention policies > Create > Workloads: Exchange + SharePoint + OneDrive > Duration: 7 years.',
    manual: false },

  // ===========================================================================
  // IDENTITY HARDENING AVANCE (family identity - extension)
  // ===========================================================================
  { id: 'ID-030', family: 'identity', title: 'SSPR registration coverage >= 95%', severity: 'medium',
    description: 'Au moins 95% des utilisateurs ont complété l\'enregistrement Self-Service Password Reset (SSPR). Pré-requis MFA + récupération autonome.',
    rationale: 'SSPR sans registration = useless. Booste également la posture MFA car partage les mêmes méthodes (authenticator app, phone).',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Authentification autonome' },
      { framework: 'CIS-M365', ref: '1.2.1',         label: 'SSPR registration enforced' },
      { framework: 'CyFun',    ref: 'PR.AC-1',       label: 'Account recovery available', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/reports/authenticationMethods/userRegistrationDetails'],
    remediation: 'Entra > Password reset > Registration > Require users to register when signing in = Yes. Suivre via Reports > Authentication methods activity.',
    manual: false },

  { id: 'ID-031', family: 'identity', title: 'Privileged Access Workstations (PAW)', severity: 'high',
    description: 'Les Global Admins / Privileged Role Admins utilisent des PAW dédiées (devices Intune-managed, hardened, sans web/email général) pour les sessions admin.',
    rationale: 'Standard Microsoft Tier 0 : un admin ne doit jamais ouvrir une URL web ou un email sur la même session que celle où il administre M365. Réduit le risque de session hijack massif.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(i)', label: 'Contrôle d\'accès admin' },
      { framework: 'ISO27001', ref: 'A.8.2',         label: 'Droits d\'accès privilégiés' },
      { framework: 'CIS-M365', ref: '1.1.10',        label: 'PAW for privileged admins' },
      { framework: 'CyFun',    ref: 'PR.AC-4',       label: 'Tier 0 admin workstation segregation', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'Provisioning PAW Intune Autopilot + CA Policy "Require compliant device" sur target role = Global Administrator. Doc Microsoft : aka.ms/PAW.',
    manual: true },

  { id: 'ID-032', family: 'identity', title: 'Tier model administration documenté', severity: 'medium',
    description: 'L\'organisation suit un Tier model d\'administration documenté (Tier 0 = Domain/Cloud Admins, Tier 1 = Servers/App Admins, Tier 2 = User Workstations) avec règles de non-cross-contamination.',
    rationale: 'Empêche un compte Tier 2 compromis d\'escalader vers Tier 0. Modèle standard MS depuis 2015, mais rarement implémenté.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(i)', label: 'Gouvernance des privilèges' },
      { framework: 'ISO27001', ref: 'A.5.15',        label: 'Contrôle d\'accès' },
      { framework: 'CIS-M365', ref: '1.1.11',        label: 'Admin tier model documented' },
      { framework: 'CyFun',    ref: 'PR.AC-4',       label: 'Privileged role tiers', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Documenter Tier 0/1/2 + sign-in restrictions par tier via CA Policies (CompliantDevice + AuthenticationStrength FIDO2 pour T0). Doc : aka.ms/Tier-Model.',
    manual: true },

  { id: 'ID-033', family: 'identity', title: 'Service Principal credentials expiration monitoring', severity: 'medium',
    description: 'Aucun Service Principal avec credentials (secret OU certificat) expirés. Aucun avec credentials > 2 ans de validité. Audit trimestriel + rotation.',
    rationale: 'Les secrets expirés cassent les intégrations (downtime). Les secrets > 2 ans = surface d\'attaque (Storm-0558 catégorie). Complète ID-026 qui ne regarde que App Registrations.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Gestion des credentials' },
      { framework: 'ISO27001', ref: 'A.5.16',        label: 'Gestion de l\'identité' },
      { framework: 'CIS-M365', ref: '1.1.12',        label: 'SP credentials rotated' },
      { framework: 'CyFun',    ref: 'PR.AC-1',       label: 'Non-human credentials managed', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/servicePrincipals'],
    remediation: 'Audit Get-MgServicePrincipal | Get-MgServicePrincipalCredential. Rotation annuelle minimum. Préférer certificats à secrets quand possible.',
    manual: false },

  { id: 'EM-012', family: 'email', title: 'Tagging visuel externe + First Contact Safety Tips', severity: 'medium',
    description: 'Les emails externes sont taggés visuellement + Safety Tips Defender actifs.',
    rationale: 'Aide les users à identifier les emails de phishing externe (BEC, impersonation).',
    references: [{ framework: 'CyFun', ref: 'PR.AT-1', label: 'Users informed of external senders', cyfunLevel: 'Important' }],
    graphEndpoints: [],
    remediation: 'EAC > Mail flow rule "Prepend external sender warning" + Defender > Anti-phishing > Enable First Contact Safety Tips.',
    manual: true },

  // ===========================================================================
  // EMAIL AVANCE (v1.2 - DANE / BIMI / Reverse DNS / External warning auto / IRM)
  // ===========================================================================
  { id: 'EM-013', family: 'email', title: 'DANE pour SMTP entrant (TLSA records)',
    severity: 'medium',
    description: 'Les records TLSA (DANE) sont publiés et signés DNSSEC pour les MX du tenant. Microsoft 365 supporte DANE en sortie depuis 2023 et impose progressivement DANE entrant pour ses partenaires.',
    rationale: 'DANE empêche les attaques MITM sur le canal SMTP en validant le certificat TLS via DNS (avec DNSSEC). Recommandé NIS2 pour les flux email transverses.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)',  label: 'Sécurisation des communications transmises' },
      { framework: 'ISO27001', ref: 'A.8.21',         label: 'Sécurité des services réseau' },
      { framework: 'CyFun',    ref: 'PR.DS-2',        label: 'Data-in-transit protected', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/domains'],
    remediation: 'Activer DNSSEC chez le registrar, publier _25._tcp.<mx>.<domain> TLSA 3 1 1 <sha256-cert> pour chaque MX. Tester via https://dane.sys4.de/smtp/<domain>.',
    manual: false },

  { id: 'EM-014', family: 'email', title: 'BIMI - Brand Indicators for Message Identification',
    severity: 'low',
    description: 'Le domaine publie un record BIMI (_bimi.<domain> TXT v=BIMI1; l=<logo-url>;) qui permet aux MUA (Gmail, Outlook, Apple Mail) d\'afficher le logo officiel à côté des emails légitimes.',
    rationale: 'Renforce la confiance utilisateur et la résistance au phishing par marquage visuel. Prérequis : DMARC p=quarantine ou p=reject. Recommandé pour les organisations communicant largement par email.',
    references: [
      { framework: 'CIS-M365', ref: 'CIS-M365 (advisory)', label: 'BIMI publishing recommended' },
      { framework: 'CyFun',    ref: 'PR.AT-1',  label: 'Awareness via verified branding', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/domains'],
    remediation: 'Publier _bimi.<domain> TXT "v=BIMI1; l=https://example.com/logo.svg; a=https://example.com/vmc.pem". VMC obligatoire pour Gmail/Yahoo (env. 1 200 €/an via DigiCert ou Entrust).',
    manual: false },

  { id: 'EM-015', family: 'email', title: 'Reverse DNS / PTR sur IPs sortantes',
    severity: 'medium',
    description: 'Tous les serveurs SMTP sortants du tenant (incluant les relais on-prem si présents) ont un PTR DNS valide et matche le HELO/EHLO annoncé. Microsoft 365 publie automatiquement les PTR pour outlook.com, mais les relais tiers doivent être contrôlés.',
    rationale: 'Sans rDNS valide, les emails sortants sont rejetés ou marqués spam par la plupart des grands fournisseurs (Gmail, Outlook, Yahoo). Pré-requis fondamental pour la délivrabilité.',
    references: [
      { framework: 'CIS-M365', ref: '6.2.7',  label: 'rDNS / FCrDNS for outbound SMTP' },
      { framework: 'CyFun',    ref: 'PR.DS-2', label: 'Communications integrity', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: ['/domains'],
    remediation: 'Configurer un PTR auprès de votre hébergeur pour chaque IP SMTP sortante. Vérifier via dig -x <ip> +short et mxtoolbox.com/SuperTool.aspx?action=ptr.',
    manual: false },

  { id: 'EM-016', family: 'email', title: 'Tag external sender automatique (ExternalInOutlook)',
    severity: 'medium',
    description: 'La policy ExternalInOutlook (Set-ExternalInOutlook -Enabled $true) est active : Outlook affiche automatiquement un tag "EXTERNAL" sur les mails externes sans nécessiter de mail flow rule.',
    rationale: 'Solution native MS plus robuste que les mail flow rules custom : tag visible dans la liste des messages et dans le panneau de lecture, fonctionne sur tous les clients Outlook (Win/Mac/Web/Mobile).',
    references: [
      { framework: 'CIS-M365', ref: '6.5.2',  label: 'External sender warning enabled' },
      { framework: 'CyFun',    ref: 'PR.AT-1', label: 'Visual external tag', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'EXO PowerShell : Set-ExternalInOutlook -Enabled $true. Aucun impact utilisateur, déploiement immédiat sur tous les emails entrants externes.',
    manual: false },

  { id: 'EM-017', family: 'email', title: 'IRM / Message Encryption activé pour usage interne',
    severity: 'medium',
    description: 'Information Rights Management (IRM) ou Office 365 Message Encryption est configuré et utilisable par les utilisateurs (au moins un template "Encrypt-only" ou "Do Not Forward" disponible dans Outlook).',
    rationale: 'Permet aux utilisateurs de protéger des emails contenant des données sensibles (PII, CB, secrets) en complément des sensitivity labels. Exigence implicite NIS2 pour les communications confidentielles.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Chiffrement des communications' },
      { framework: 'ISO27001', ref: 'A.8.24',        label: 'Utilisation de la cryptographie' },
      { framework: 'CyFun',    ref: 'PR.DS-2',       label: 'Email encryption available', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/security/informationProtection/sensitivityLabels'],
    remediation: 'Activer Azure RMS via Purview > Information Protection > Configure encryption. Publier au moins un sensitivity label avec "Encrypt-only" pour les end users.',
    manual: false },

  { id: 'DP-016', family: 'data', title: 'Teams external collaboration restreinte', severity: 'medium',
    description: 'Teams external access est limité aux domaines whitelistés + anonymous join désactivé sur groupes sensibles.',
    rationale: 'Bloque les exploitations Teams federation (TokenGuard bypass de 2024).',
    references: [{ framework: 'CyFun', ref: 'PR.AC-4', label: 'Teams federation controlled', cyfunLevel: 'Important' }],
    graphEndpoints: [],
    remediation: 'Teams Admin > Users > External access > Allow specific domains. Meeting policies = Disable anonymous join.',
    manual: true },

  { id: 'DP-017', family: 'data', title: 'Revue mensuelle SPO External Sharing', severity: 'medium',
    description: 'External Sharing Report exécuté mensuellement + liens "Anyone" expirent automatiquement < 30 jours.',
    rationale: 'Les liens externes oubliés sont la 1ère cause de fuites M365 confidentielles.',
    references: [{ framework: 'CyFun', ref: 'PR.DS-5', label: 'External sharing reviewed periodically', cyfunLevel: 'Important' }],
    graphEndpoints: [],
    remediation: 'SPO Admin > Policies > Sharing > Expire links Anyone = 14 days. Reports > External Sharing > Run monthly.',
    manual: true },

  { id: 'LG-012', family: 'logs', title: 'Mailbox audit activé tenant-wide', severity: 'high',
    description: 'Mailbox auditing est activé au niveau organization (Get-OrganizationConfig.AuditDisabled = False).',
    rationale: 'Sans audit mailbox, impossible d investiguer un compte compromis (BEC, exfiltration emails).',
    references: [{ framework: 'CyFun', ref: 'DE.CM-3', label: 'Mailbox actions audited', cyfunLevel: 'Important' }],
    graphEndpoints: [],
    remediation: 'EXO PowerShell : Set-OrganizationConfig -AuditDisabled $false. Vérifier Get-MailboxAuditBypassAssociation.',
    manual: true },

  { id: 'LG-013', family: 'logs', title: 'Risky sign-ins low + high-risk sur 7 jours', severity: 'high',
    description: 'Le nombre de risky sign-ins high-risk sur 7 jours est < 5 (au-delà = campagne phishing ciblée probable).',
    rationale: 'Risky sign-ins haut = compte sous attaque. Indicateur proactif Identity Protection.',
    references: [{ framework: 'CyFun', ref: 'DE.CM-7', label: 'Risky sign-ins detected', cyfunLevel: 'Important' }],
    graphEndpoints: ['/identityProtection/riskDetections'],
    remediation: 'Defender XDR > Investigation risky sign-ins. Si > 5 high-risk/semaine : campagne phishing, déclencher IR.',
    manual: false },

  { id: 'LG-014', family: 'logs', title: 'Audit hebdo des changements de rôles admin', severity: 'high',
    description: 'Les changements de role privilégié sur 7 jours sont review (< 5 = normal, > 5 = à investiguer).',
    rationale: 'Privilege escalation = TTPs majeure des attaquants après compromis initial.',
    references: [{ framework: 'CyFun', ref: 'DE.AE-3', label: 'Privilege escalations monitored', cyfunLevel: 'Important' }],
    graphEndpoints: ['/auditLogs/directoryAudits'],
    remediation: 'Review hebdo des changements role. Si > 3 ajouts/semaine : alerter SOC. PIM (P2) ajoute approbation workflow.',
    manual: false },

  // ===========================================================================
  // CyFun Essential gap closure (Phase 49.B - 2026.06.7)
  // 10 nouveaux contrôles Essential : DEF/PAM/PIM/IRM/CC/DR/ENC.
  // ===========================================================================
  { id: 'LG-020', family: 'logs', title: 'Defender XDR - règles de détection custom', severity: 'high',
    description: 'Au moins 5 custom détection rules deployees dans Defender XDR Advanced Hunting (exfiltration, lateral movement, OAuth grants risques).',
    rationale: 'CyFun DE.AE-3.6 (Essential) exige correlation et détection avancee au-dela des policies par defaut.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Gestion des incidents' },
      { framework: 'CIS-M365', ref: '2.4.5',         label: 'Custom Détections (Defender XDR)' },
      { framework: 'CyFun',    ref: 'DE.AE-3',       label: 'Aggregated event data (DE.AE-3.6 Essential)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: ['/beta/security/rules/detectionRules'],
    remediation: 'Defender XDR > Hunting > Custom détections. 5+ règles : exfiltration, lateral movement, suspicious admin, OAuth grants, mass download.',
    manual: false },

  { id: 'LG-021', family: 'logs', title: 'Defender for Identity - comptes sensibles tagges', severity: 'high',
    description: 'Les comptes Global Admin + tier-0 sont tagges Sensitive dans Defender for Identity pour monitoring renforce.',
    rationale: 'CyFun DE.CM-1.6 (Essential) exige monitoring comportemental avance.',
    references: [
      { framework: 'CIS-M365', ref: '2.4.3',   label: 'Threat investigations - Defender for Identity' },
      { framework: 'CyFun',    ref: 'DE.CM-1', label: 'Network monitored (DE.CM-1.6 = behaviour analytics)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'Defender XDR > Settings > Identities > Sensitive : tagger Global Admin, Privileged Role Admin, Tier-0 service accounts.',
    manual: true },

  { id: 'LG-022', family: 'logs', title: 'Attack Simulator - campagnes trimestrielles', severity: 'medium',
    description: 'Au moins 1 campagne Attack Simulator (phishing simule) executee dans les 90 derniers jours.',
    rationale: 'CyFun PR.AT-2.4 (Essential) exige des tests réguliers de la vigilance des users. CIS-M365 2.1.11 attend une cadence trimestrielle.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(g)', label: 'Cyber-hygiene et formation' },
      { framework: 'CIS-M365', ref: '2.1.11',        label: 'Attack simulation training' },
      { framework: 'CyFun',    ref: 'PR.AT-2',       label: 'Privileged users trained (PR.AT-2.4 Essential)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: ['/beta/security/attackSimulation/simulations'],
    remediation: 'Defender > Email & collaboration > Attack simulation training. 1 campagne/trimestre, cibler tous les users + admins separes.',
    manual: false },

  { id: 'LG-023', family: 'logs', title: 'Réponse automatisee sur les custom détections', severity: 'high',
    description: 'Au moins 3 custom détection rules ont une action automatisee (isoler device, désactiver user, AIR playbook).',
    rationale: 'CyFun DE.DP-4.4 (Essential) exige des processus de détection raffines et automatises.',
    references: [
      { framework: 'CyFun', ref: 'DE.DP-4', label: 'Event détection automated (DE.DP-4.4 Essential)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: ['/beta/security/rules/detectionRules'],
    remediation: 'Pour chaque règle critique, configurer Actions : isolate device, disable user, run AIR investigation playbook.',
    manual: false },

  { id: 'ID-034', family: 'identity', title: 'Privileged Access Management (PAM) workflow', severity: 'high',
    description: 'PAM active dans M365 Compliance, les taches privilegiees (eDiscovery, mailbox export) exigent approbation explicite.',
    rationale: 'CyFun PR.AC-4.7 (Essential) exige workflows formels pour accès privilégiés.',
    references: [
      { framework: 'CIS-M365', ref: '3.8.1',   label: 'Privileged Access Management' },
      { framework: 'CyFun',    ref: 'PR.AC-4', label: 'Privileged tasks approved (PR.AC-4.7 Essential)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'M365 Admin > Settings > Org settings > Security & privacy > Privileged access. PAM + 3 policies. Nécessite M365 E5.',
    manual: true },

  { id: 'ID-035', family: 'identity', title: 'PIM - assignments eligibles vs permanents', severity: 'critical',
    description: 'Au moins 80% des assignments de roles privilégiés sont eligibles (JIT) plutot que permanents.',
    rationale: 'CyFun PR.AC-4.7 (Essential) exige JIT/JEA. Les assignments permanents augmentent le blast radius.',
    references: [
      { framework: 'NIS2',  ref: 'Art. 21(2)(i)', label: 'Sécurité accès' },
      { framework: 'CyFun', ref: 'PR.AC-4',       label: 'Least privilege + JIT (PR.AC-4.7 Essential)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: ['/roleManagement/directory/roleEligibilityScheduleInstances', '/roleManagement/directory/roleAssignmentScheduleInstances'],
    remediation: 'Entra ID > PIM > Azure AD roles. Convertir Assigned -> Eligible avec approval + MFA. 0 admin permanent (sauf 2 break-glass).',
    manual: false },

  { id: 'DP-021', family: 'data', title: 'Insider Risk Management - connecteur HR', severity: 'medium',
    description: 'IRM active avec HR connector alimente (terminations, job changes) et 1 policy Departing user data theft.',
    rationale: 'CyFun PR.DS-5.4 (Essential) exige détection de fuite données avec signaux HR.',
    references: [
      { framework: 'CIS-M365', ref: '3.4.1',   label: 'Insider Risk Management policies' },
      { framework: 'CyFun',    ref: 'PR.DS-5', label: 'Insider threat (PR.DS-5.4 Essential = HR-based)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'Purview > IRM > Connectors > HR connector. Import CSV hebdo terminations/job changes. Policy Departing user data theft.',
    manual: true },

  { id: 'DP-022', family: 'data', title: 'M365 Backup - drill de restauration annuel', severity: 'high',
    description: 'Un drill de restauration M365 Backup (Exchange + SPO + OneDrive) a ete execute dans les 12 derniers mois et documente.',
    rationale: 'CyFun RC.RP-1.4 (Essential) + NIS2 art. 21(2)(c) exigent un plan de continuite teste.',
    references: [
      { framework: 'NIS2',  ref: 'Art. 21(2)(c)', label: 'Continuite activites, sauvegardes' },
      { framework: 'CyFun', ref: 'RC.RP-1',       label: 'Recovery plan executed (RC.RP-1.4 Essential)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'M365 Backup > Restore points. Trimestre minimum : restaurer 1 mailbox + 1 SPO + 1 OneDrive J-7. Mesurer RTO/RPO. PV signe.',
    manual: true },

  { id: 'DP-023', family: 'data', title: 'Customer Key pour Service Encryption', severity: 'medium',
    description: 'Customer Key active pour Exchange Online + SharePoint Online avec DEP (Data Encryption Policy) assignee.',
    rationale: 'CyFun PR.DS-1.7 (Essential) + NIS2 art. 21(2)(h) exigent chiffrement avec cles gérées client.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Cryptographie et chiffrement' },
      { framework: 'CIS-M365', ref: '3.5.2',         label: 'Customer Key for service encryption' },
      { framework: 'CyFun',    ref: 'PR.DS-1',       label: 'Data at rest (PR.DS-1.7 Essential = customer keys)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: '2 Azure Key Vaults (geo-redondance), 2 cles RSA-2048/KV, DEP via Exchange Online PS + SPO. Nécessite Office 365 Advanced Compliance.',
    manual: true },

  { id: 'COL-006', family: 'collab', title: 'Communication Compliance - policy langage offensant', severity: 'medium',
    description: 'Au moins une policy Communication Compliance active (offensive language, harassment) avec reviewers assignes et cadence hebdomadaire.',
    rationale: 'CyFun DE.CM-3.4 (Essential) exige monitoring du comportement personnel pour détecter risques internes.',
    references: [
      { framework: 'CIS-M365', ref: '3.2.2',   label: 'Communication Compliance policies' },
      { framework: 'CyFun',    ref: 'DE.CM-3', label: 'Personnel monitored (DE.CM-3.4 Essential)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'Purview > Communication Compliance > Create policy Detect inappropriate content. Scope Teams + Outlook. Reviewers RH + Compliance officer.',
    manual: true },

  // ===========================================================================
  // Phase 49.D - Batch +16 checks Exchange Online (CIS section 6.x)
  // ===========================================================================
  { id: 'EXO-001', family: 'email', title: 'Mailbox forwarding externes bloquees', severity: 'high',
    description: 'Aucune Inbox rule externe (BEC)',
    rationale: 'CIS-M365 6.1.3 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.1.3', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.DS-5', label: 'CyFun PR.DS-5 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/beta/users/{id}/messages/rules'],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-002', family: 'email', title: 'Outlook add-ins install bloquee', severity: 'medium',
    description: 'Add-ins restreints aux admins',
    rationale: 'CIS-M365 6.1.4 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.1.4', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.AC-4', label: 'CyFun PR.AC-4 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-003', family: 'email', title: 'Modern Authentication enforce EXO', severity: 'critical',
    description: 'OAuth2 only (vs Basic)',
    rationale: 'CIS-M365 6.2.1 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.2.1', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.AC-1', label: 'CyFun PR.AC-1 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-004', family: 'email', title: 'Basic Auth bloque via CA', severity: 'critical',
    description: 'Legacy auth bloque',
    rationale: 'CIS-M365 6.2.2 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.2.2', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.AC-7', label: 'CyFun PR.AC-7 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/identity/conditionalAccess/policies'],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-005', family: 'email', title: 'Outbound Spam Filter notify', severity: 'high',
    description: 'Notify si user atteint quota',
    rationale: 'CIS-M365 6.2.3 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.2.3', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'DE.AE-2', label: 'CyFun DE.AE-2 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-006', family: 'email', title: 'Inbound connectors restricted by IP', severity: 'high',
    description: 'TLS + SenderIP allow-list',
    rationale: 'CIS-M365 6.2.4 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.2.4', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.AC-5', label: 'CyFun PR.AC-5 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-007', family: 'email', title: 'Auto-forwarding externe bloque', severity: 'critical',
    description: 'Anti-exfiltration au transport',
    rationale: 'CIS-M365 6.2.5 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.2.5', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.DS-5', label: 'CyFun PR.DS-5 (Essential)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-008', family: 'email', title: 'DMARC reject domaine principal', severity: 'high',
    description: 'p=reject + rua/ruf',
    rationale: 'CIS-M365 6.4.1 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.4.1', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.AC-7', label: 'CyFun PR.AC-7 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-009', family: 'email', title: 'Anti-Spam outbound thresholds', severity: 'medium',
    description: 'Limite externe <= 500/h',
    rationale: 'CIS-M365 6.5.3 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.5.3', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'DE.AE-2', label: 'CyFun DE.AE-2 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-010', family: 'email', title: 'Anti-Malware common attachments', severity: 'high',
    description: 'Filter exe/scr/vbs/ps1',
    rationale: 'CIS-M365 6.5.5 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.5.5', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.IP-1', label: 'CyFun PR.IP-1 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-011', family: 'email', title: 'Common Attachment includes executables', severity: 'high',
    description: 'Liste extensions complete',
    rationale: 'CIS-M365 6.5.6 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.5.6', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.IP-1', label: 'CyFun PR.IP-1 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-012', family: 'email', title: 'OWA storage/attachment limits', severity: 'low',
    description: 'AttachmentLimit <= 25MB',
    rationale: 'CIS-M365 6.5.7 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.5.7', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.DS-3', label: 'CyFun PR.DS-3 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-013', family: 'email', title: 'Quarantine notify quotidien', severity: 'medium',
    description: 'EsnEnabled + freq 1 jour',
    rationale: 'CIS-M365 6.6.3 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.6.3', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.IP-1', label: 'CyFun PR.IP-1 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-014', family: 'email', title: 'Transport rule executable blocked', severity: 'high',
    description: 'Rule AttachmentHasExecutableContent',
    rationale: 'CIS-M365 6.7.1 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.7.1', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.IP-1', label: 'CyFun PR.IP-1 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-015', family: 'email', title: 'Calendar sharing externe restreint', severity: 'low',
    description: 'Pas anonyme:CalendarSharing',
    rationale: 'CIS-M365 6.1.5 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.1.5', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.DS-5', label: 'CyFun PR.DS-5 (Basic)', cyfunLevel: 'Basic' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'EXO-016', family: 'email', title: 'ZAP (Zero-hour Auto Purge) actif', severity: 'high',
    description: 'ZapEnabled = true',
    rationale: 'CIS-M365 6.5.8 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '6.5.8', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'DE.AE-2', label: 'CyFun DE.AE-2 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },

  // ===========================================================================
  // Phase 49.E - Batch +13 checks Purview Compliance (CIS section 3.x)
  // ===========================================================================
  { id: 'PUR-001', family: 'data', title: 'Activity Alerts configures Purview', severity: 'medium',
    description: 'Alertes sur actions sensibles',
    rationale: 'CIS-M365 3.1.2 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.1.2', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'DE.AE-3', label: 'CyFun DE.AE-3 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-002', family: 'data', title: 'Mailbox auditing all mailboxes', severity: 'high',
    description: 'Audit mailbox 100% périmètre',
    rationale: 'CIS-M365 3.1.3 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.1.3', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'DE.AE-3', label: 'CyFun DE.AE-3 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-003', family: 'data', title: 'DLP policies scope GDPR-aware', severity: 'high',
    description: 'DLP cibles données personnelles UE',
    rationale: 'CIS-M365 3.2.1 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.2.1', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.DS-5', label: 'CyFun PR.DS-5 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-004', family: 'data', title: 'eDiscovery Premium configure', severity: 'medium',
    description: 'eDiscovery Premium + custodian',
    rationale: 'CIS-M365 3.3.1 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.3.1', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.IP-10', label: 'CyFun PR.IP-10 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-005', family: 'data', title: 'Data Lifecycle Records Management', severity: 'medium',
    description: 'Retention legale records mgmt',
    rationale: 'CIS-M365 3.3.2 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.3.2', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.IP-6', label: 'CyFun PR.IP-6 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-006', family: 'data', title: 'Information Barriers segments', severity: 'medium',
    description: 'Segmentation departements',
    rationale: 'CIS-M365 3.5.1 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(j)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.5.1', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.AC-5', label: 'CyFun PR.AC-5 (Essential)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-007', family: 'data', title: 'Compliance Manager improvement actions', severity: 'low',
    description: 'Plan reduction risk score',
    rationale: 'CIS-M365 3.6.1 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.6.1', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'ID.RA-5', label: 'CyFun ID.RA-5 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-008', family: 'data', title: 'Microsoft Purview Data Map sources', severity: 'medium',
    description: 'Data map sources scannees',
    rationale: 'CIS-M365 3.7.1 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.7.1', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'ID.AM-3', label: 'CyFun ID.AM-3 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-009', family: 'data', title: 'Sensitivity Labels auto-classification', severity: 'high',
    description: 'Auto-label DLP enrichi',
    rationale: 'CIS-M365 3.x - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.x', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.DS-5', label: 'CyFun PR.DS-5 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-010', family: 'data', title: 'Insider Risk - policies multiples', severity: 'medium',
    description: '3+ policies IRM actives',
    rationale: 'CIS-M365 3.4.1 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(h)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.4.1', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.DS-5', label: 'CyFun PR.DS-5 (Essential)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-011', family: 'data', title: 'Communication Compliance regulatory', severity: 'medium',
    description: 'Policy regulatory compliance',
    rationale: 'CIS-M365 3.2.2 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(b)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.2.2', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'DE.CM-3', label: 'CyFun DE.CM-3 (Essential)', cyfunLevel: 'Essential' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-012', family: 'data', title: 'Audit Search Premium 1 year retention', severity: 'medium',
    description: 'Premium audit retention 1 an',
    rationale: 'CIS-M365 3.1.1 - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.1.1', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.PT-1', label: 'CyFun PR.PT-1 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },
  { id: 'PUR-013', family: 'data', title: 'Retention policies labels published', severity: 'medium',
    description: 'Labels publies + auto-applied',
    rationale: 'CIS-M365 3.3.x - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: '3.3.x', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.IP-4', label: 'CyFun PR.IP-4 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Évidence du check PowerShell associe.',
    manual: true },

  // ===========================================================================
  // Phase 49.F - Chaine d'approvisionnement NIS2 art. 21(2)(d)
  // ===========================================================================
  { id: 'APP-005', family: 'apps', title: 'OAuth grants - third-party risk score', severity: 'high',
    description: 'OAuth apps risk score < high',
    rationale: 'CIS-M365 CIS-M365 (advisory) - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: 'CIS-M365 (advisory)', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'ID.SC-4', label: 'CyFun ID.SC-4 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/v1.0/oauth2PermissionGrants'],
    remediation: 'Voir Recommendation dans Evidence du check PowerShell associe.',
    manual: true },
  { id: 'APP-006', family: 'apps', title: 'App consent policies bloquent risky', severity: 'high',
    description: 'User consent off ou policy stricte',
    rationale: 'CIS-M365 CIS-M365 (advisory) - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: 'CIS-M365 (advisory)', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'ID.SC-4', label: 'CyFun ID.SC-4 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/v1.0/policies/permissionGrantPolicies'],
    remediation: 'Voir Recommendation dans Evidence du check PowerShell associe.',
    manual: true },
  { id: 'APP-007', family: 'apps', title: 'Verified Publisher only pour apps tierces', severity: 'medium',
    description: 'Apps non-verified bloquees',
    rationale: 'CIS-M365 CIS-M365 (advisory) - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(d)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: 'CIS-M365 (advisory)', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'ID.SC-1', label: 'CyFun ID.SC-1 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: ['/v1.0/applications'],
    remediation: 'Voir Recommendation dans Evidence du check PowerShell associe.',
    manual: true },
  { id: 'APP-008', family: 'apps', title: 'Periodic review OAuth tokens emis', severity: 'medium',
    description: 'Review trimestrielle tokens emis',
    rationale: 'CIS-M365 CIS-M365 (advisory) - couverture requise pour audit complet.',
    references: [
      { framework: 'NIS2',     ref: 'Art. 21(2)(e)', label: 'Mesure technique NIS2' },
      { framework: 'CIS-M365', ref: 'CIS-M365 (advisory)', label: 'CIS Microsoft 365 Foundations v3' },
      { framework: 'CyFun',    ref: 'PR.IP-11', label: 'CyFun PR.IP-11 (Important)', cyfunLevel: 'Important' },
    ],
    graphEndpoints: [],
    remediation: 'Voir Recommendation dans Evidence du check PowerShell associe.',
    manual: true },
];

/** Indexe le catalogue par ID pour accès rapide. */
export const controlsById: Record<string, ControlDefinition> = Object.fromEntries(
  controls.map((c) => [c.id, c]),
);

/** Liste des libelles affichables des familles. */
export const familyLabels: Record<ControlDefinition['family'], string> = {
  identity: 'Identite & acces',
  email:    'Email & anti-phishing',
  data:     'Protection des données',
  logs:     'Logs & monitoring',
  endpoint: 'Endpoints & devices',
  collab:   'Collaboration (Teams)',
  apps:     'Apps & Power Platform',
};

/** Couleurs Fluent associees aux familles (pour badges / charts). */
export const familyColorTokens: Record<ControlDefinition['family'], string> = {
  identity: 'colorPaletteRoyalBlueBackground2',
  email:    'colorPaletteBerryBackground2',
  data:     'colorPaletteTealBackground2',
  logs:     'colorPaletteMarigoldBackground2',
  endpoint: 'colorPalettePurpleBackground2',
  collab:   'colorPaletteSeafoamBackground2',
  apps:     'colorPaletteForestBackground2',
};

/** Poids d'une severite pour le calcul de score. */
export const severityWeight: Record<ControlDefinition['severity'], number> = {
  critical: 4,
  high:     3,
  medium:   2,
  low:      1,
};
